import { tr, useArkmeLocale } from './locale.js'
import { ArkmeRightPanelHeader } from './ArkmeRightPanelHeader.js'
import {
  memo, useCallback, useEffect, useRef, useState,
  type CSSProperties, type MouseEventHandler, type ReactNode, type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import {
  IconEllipsisOutline16, type MenuEntry,
} from '@deepseek-ai/dsh-client-ui-primitives'
import { ArrowLeft } from '@phosphor-icons/react/dist/icons/ArrowLeft'
import { ArrowUp } from '@phosphor-icons/react/dist/icons/ArrowUp'
import { CaretRight } from '@phosphor-icons/react/dist/icons/CaretRight'
import { DownloadSimple } from '@phosphor-icons/react/dist/csr/DownloadSimple'
import { Plus } from '@phosphor-icons/react/dist/icons/Plus'
import { Prohibit } from '@phosphor-icons/react/dist/icons/Prohibit'
import { Sparkle } from '@phosphor-icons/react/dist/icons/Sparkle'
import { X } from '@phosphor-icons/react/dist/icons/X'
import qrcode from 'qrcode-generator'
import type {
  ArkmeConversationMemberItem,
  ArkmeGroupActionTarget,
  ArkmeGroupAiPolishMutationResult,
  ArkmeGroupAiPolishRule,
  ArkmeGroupAiPolishRuleCandidate,
  ArkmeGroupAiPolishSnapshot,
  ArkmeGroupAiPolishThreadMessage,
  ArkmeGroupCommandResult,
  ArkmeGroupMemberAddResult,
  ArkmeGroupBotCandidateList,
  ArkmeGroupMemberCandidate,
  ArkmeGroupMemberCandidateGroup,
  ArkmeGroupMemberCandidateList,
  ArkmeGroupInvitePreview,
  ArkmeGroupJoinRestrictionMutationResult,
  ArkmeGroupJoinRestrictionPage,
  ArkmeGroupNotificationResult,
  ArkmeGroupProjectionResult,
  ArkmeGroupSettingsSnapshot,
  ArkmeSourceItem,
} from '../types.js'
import { arkmeConversationMembers } from './conversation-members-store.js'
import { useConversationMembers } from './use-conversation-members.js'
import { callArkme } from './api.js'
import { ArkmeGroupSelfNicknameDialog } from './ArkmeGroupSelfNicknameDialog.js'
import { ArkmeConfirmDialog } from './ArkmeConfirmDialog.js'
import { isArkmeRequestAbort, retryArkmeRead } from './read-retry.js'
import { arkmeSourceIdentityKey } from './source-identity.js'
import { ArkmeMark } from './ArkmeFooterAction.js'
import { arkmeTheme } from './arkme-theme.js'
import { useArkmeAvatarImage } from './use-arkme-avatar-image.js'
import { ArkmeDshMenu } from './ArkmeDshMenu.js'
import { useResizableNoteDetail } from './use-resizable-note-detail.js'

const colors = {
  panel: arkmeTheme.layer2,
  subtle: arkmeTheme.subtle,
  text: arkmeTheme.text,
  secondary: arkmeTheme.secondary,
  border: arkmeTheme.border,
  primary: arkmeTheme.info,
}

const GROUP_SETTINGS_MENU_WIDTH = 248
const AI_POLISH_PANEL_WIDTH = 408
const GROUP_JOIN_RESTRICTIONS_PANEL_WIDTH = 320

export const ARKME_GROUP_HEADER_ICON_COLOR = arkmeTheme.secondary

export const ARKME_CONVERSATION_HEADER_ACTIONS_STYLE: CSSProperties = {
  marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4,
}

export const ARKME_CONVERSATION_HEADER_BUTTON_STYLE: CSSProperties = {
  width: 28, height: 28, padding: 2, boxSizing: 'border-box', border: 0, borderRadius: 28, background: 'transparent',
  color: 'var(--dsw-alias-label-secondary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  flex: 'none', cursor: 'pointer', appearance: 'none',
}

export const ARKME_CONVERSATION_SETTINGS_MENU_WIDTH = GROUP_SETTINGS_MENU_WIDTH

export const ARKME_CONVERSATION_SETTINGS_MENU_SCRIM_STYLE: CSSProperties = {
  position: 'absolute', inset: 0, zIndex: 9,
}

export const ARKME_CONVERSATION_SETTINGS_POPOVER_STYLE: CSSProperties = {
  position: 'absolute', zIndex: 10, width: GROUP_SETTINGS_MENU_WIDTH, maxWidth: 'calc(100% - 24px)', padding: '6px 8px',
  borderRadius: 4, background: colors.panel, boxShadow: '0 4px 10px rgba(0,0,0,.1)', boxSizing: 'border-box',
}

export const ARKME_CONVERSATION_SETTINGS_MENU_ROW_STYLE: CSSProperties = {
  width: '100%', height: 32, border: 0, borderRadius: 4, background: 'transparent', padding: '2px 8px',
  display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', color: colors.text,
  cursor: 'pointer', fontSize: 14, lineHeight: '20px', whiteSpace: 'nowrap', boxSizing: 'border-box',
}

export const ARKME_CONVERSATION_SETTINGS_MENU_STATUS_STYLE: CSSProperties = {
  ...ARKME_CONVERSATION_SETTINGS_MENU_ROW_STYLE,
  color: colors.secondary,
  cursor: 'default',
}

const asset = (value: string) => `data:image/svg+xml;base64,${value}`
// These are the production desktop-client assets, embedded so the published plugin remains self-contained.
const icons = {
  // Same asset as the mobile group settings nickname action (record_func_edit.svg).
  selfNickname: asset('PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDIwIDIwIiBmaWxsPSJub25lIj4KPHBhdGggZD0iTTIgMThIMTgiIHN0cm9rZT0iI0QyRDJEMiIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPgo8cGF0aCBkPSJNMTEuODk5MyAyLjcwNzExQzEyLjI4OTkgMi4zMTY1OCAxMi45MjMgMi4zMTY1OCAxMy4zMTM2IDIuNzA3MTFMMTYuMTQyIDUuNTM1NTNDMTYuNTMyNSA1LjkyNjA2IDE2LjUzMjUgNi41NTkyMiAxNi4xNDIgNi45NDk3NUw4Ljk1Mzk0IDE0LjEzNzhDOC43ODM4MSAxNC4zMDc5IDguNTU4MDYgMTQuNDExIDguMzE4MDggMTQuNDI4MUw1LjI3MjA4IDE0LjY0NTdDNC42NjQ3OSAxNC42ODkxIDQuMTU5OTkgMTQuMTg0MyA0LjIwMzM3IDEzLjU3N0w0LjQyMDk0IDEwLjUzMUM0LjQzODA5IDEwLjI5MSA0LjU0MTE3IDEwLjA2NTMgNC43MTEzIDkuODk1MTVMMTEuODk5MyAyLjcwNzExWiIgc3Ryb2tlPSIjRDJEMkQyIiBzdHJva2Utd2lkdGg9IjEuNSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIi8+Cjwvc3ZnPg=='),
  rename: asset('PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTguODAwNzggMS42MDAxSDEwLjAwMDhNMTAuMDAwOCAxLjYwMDFIMTEuMjAwOE0xMC4wMDA4IDEuNjAwMVYxNC40MDAxTTguODAwNzggMTQuNDAwMUgxMS4yMDA4IiBzdHJva2U9IiNEMkQyRDIiIHN0cm9rZS13aWR0aD0iMS4yIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPHBhdGggZD0iTTcuODk5NjEgNEgzLjE5OTYxQzIuMzE1OTUgNCAxLjU5OTYxIDQuNzE2MzQgMS41OTk2MSA1LjZWMTAuNEMxLjU5OTYxIDExLjI4MzcgMi4zMTU5NSAxMiAzLjE5OTYxIDEySDcuODk5NjFNMTEuOTk5NiA0SDEyLjc5OTZDMTMuNjgzMyA0IDE0LjM5OTYgNC43MTYzNCAxNC4zOTk2IDUuNlYxMC40QzE0LjM5OTYgMTEuMjgzNyAxMy42ODMzIDEyIDEyLjc5OTYgMTJIMTEuOTk5NiIgc3Ryb2tlPSIjRDJEMkQyIiBzdHJva2Utd2lkdGg9IjEuMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxwYXRoIGQ9Ik00LjgwMDc4IDhMNi40MDA3OCA4IiBzdHJva2U9IiNEMkQyRDIiIHN0cm9rZS13aWR0aD0iMS4yIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+Cg=='),
  exit: asset('PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMSIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDIxIDIwIiBmaWxsPSJub25lIj4KPHBhdGggZD0iTTMuNSA3VjRDMy41IDMuNDQ3NzIgMy45NDc3MiAzIDQuNSAzSDcuNSIgc3Ryb2tlPSIjRDJEMkQyIiBzdHJva2Utd2lkdGg9IjEuNSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxwYXRoIGQ9Ik0zLjUgMTNWMTZDMy41IDE2LjU1MjMgMy45NDc3MiAxNyA0LjUgMTdINy41IiBzdHJva2U9IiNEMkQyRDIiIHN0cm9rZS13aWR0aD0iMS41IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPHBhdGggZD0iTTE3LjUgN1Y0QzE3LjUgMy40NDc3MiAxNy4wNTIzIDMgMTYuNSAzSDEzLjUiIHN0cm9rZT0iI0QyRDJEMiIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8cGF0aCBkPSJNMTcuNSAxM0wxNS41IDE1TDEzLjUgMTciIHN0cm9rZT0iI0QyRDJEMiIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8cGF0aCBkPSJNMTMuNSAxM0wxNS41IDE1TDE3LjUgMTciIHN0cm9rZT0iI0QyRDJEMiIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4='),
  notice: asset('PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDIwIDIwIiBmaWxsPSJub25lIj4KPHBhdGggZD0iTTEwLjAxNzUgMi40MjQ4QzcuMjU5MTQgMi40MjQ4IDUuMDE3NDcgNC42NjY0NyA1LjAxNzQ3IDcuNDI0OFY5LjgzMzE0QzUuMDE3NDcgMTAuMzQxNSA0LjgwMDgxIDExLjExNjUgNC41NDI0NyAxMS41NDk4TDMuNTg0MTQgMTMuMTQxNUMyLjk5MjQ3IDE0LjEyNDggMy40MDA4MSAxNS4yMTY1IDQuNDg0MTQgMTUuNTgzMUM4LjA3NTgxIDE2Ljc4MzEgMTEuOTUwOCAxNi43ODMxIDE1LjU0MjUgMTUuNTgzMUMxNi41NTA4IDE1LjI0OTggMTYuOTkyNSAxNC4wNTgxIDE2LjQ0MjUgMTMuMTQxNUwxNS40ODQxIDExLjU0OThDMTUuMjM0MSAxMS4xMTY1IDE1LjAxNzUgMTAuMzQxNSAxNS4wMTc1IDkuODMzMTRWNy40MjQ4QzE1LjAxNzUgNC42NzQ4IDEyLjc2NzUgMi40MjQ4IDEwLjAxNzUgMi40MjQ4WiIgc3Ryb2tlPSIjRDJEMkQyIiBzdHJva2Utd2lkdGg9IjEuNCIgc3Ryb2tlLW1pdGVybGltaXQ9IjEwIiBzdHJva2UtbGluZWNhcD0icm91bmQiLz4KPHBhdGggZD0iTTExLjU1OTkgMi42NjcxOUMxMS4zMDE2IDIuNTkyMTkgMTEuMDM0OSAyLjUzMzg1IDEwLjc1OTkgMi41MDA1MkM5Ljk1OTkgMi40MDA1MiA5LjE5MzIzIDIuNDU4ODUgOC40NzY1NiAyLjY2NzE5QzguNzE4MjMgMi4wNTA1MiA5LjMxODIzIDEuNjE3MTkgMTAuMDE4MiAxLjYxNzE5QzEwLjcxODIgMS42MTcxOSAxMS4zMTgyIDIuMDUwNTIgMTEuNTU5OSAyLjY2NzE5WiIgc3Ryb2tlPSIjRDJEMkQyIiBzdHJva2Utd2lkdGg9IjEuMjUiIHN0cm9rZS1taXRlcmxpbWl0PSIxMCIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxwYXRoIGQ9Ik0xMi41MTU2IDE1Ljg4MjhDMTIuNTE1NiAxNy4yNTc4IDExLjM5MDYgMTguMzgyOCAxMC4wMTU2IDE4LjM4MjhDOS4zMzIyOSAxOC4zODI4IDguNjk4OTYgMTguMDk5NSA4LjI0ODk2IDE3LjY0OTVDNy43OTg5NiAxNy4xOTk1IDcuNTE1NjIgMTYuNTY2MSA3LjUxNTYyIDE1Ljg4MjgiIHN0cm9rZT0iI0QyRDJEMiIgc3Ryb2tlLXdpZHRoPSIxLjI1IiBzdHJva2UtbWl0ZXJsaW1pdD0iMTAiLz4KPC9zdmc+'),
  members: asset('PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyLjY5MzQgMi41QzE0Ljk5NDIgMi41MDAzNSAxNi44NTk0IDQuMzY2MDIgMTYuODU5NCA2LjY2Njk5QzE2Ljg1OTMgNy44OTY2NSAxNi4zMjU4IDkuMDAxMDYgMTUuNDc4NSA5Ljc2MzY3QzE1LjQ3NDEgOS43Njc2OCAxNS40NzUxIDkuNzc0ODEgMTUuNDgwNSA5Ljc3NzM0QzE3Ljc3IDEwLjgzMjcgMTkuMzYwMiAxMy4xNDc2IDE5LjM2MDQgMTUuODM0QzE5LjM2MDIgMTYuMjkzOSAxOC45ODYyIDE2LjY2NzYgMTguNTI2NCAxNi42NjhINi44NjAzNUM2LjQwMDIyIDE2LjY2OCA2LjAyNjU0IDE2LjI5NDEgNi4wMjYzNyAxNS44MzRDNi4wMjY1IDEzLjE0NzMgNy42MTYyOCAxMC44MzI2IDkuOTA2MjUgOS43NzczNEM5LjkxMTY2IDkuNzc0ODEgOS45MTI2NSA5Ljc2NzY4IDkuOTA4MiA5Ljc2MzY3QzkuMDYwNjIgOS4wMDEwNCA4LjUyNjQ2IDcuODk2OTEgOC41MjYzNyA2LjY2Njk5QzguNTI2MzcgNC4zNjU4MSAxMC4zOTIyIDIuNSAxMi42OTM0IDIuNVpNNi43NTk3NyA1QzYuOTU2ODYgNS4wMDAwNiA3LjE0OTcgNS4wMTg0MSA3LjMzNjkxIDUuMDUyNzNDNy4zMDAxMiA1LjMwNzU5IDcuMjc5MzEgNS41Njc5NyA3LjI3OTMgNS44MzMwMUM3LjI3OTMgNi4wMDkyMyA3LjI5MTE0IDYuMTgzNTMgNy4zMDc2MiA2LjM1NTQ3QzcuMTMzNDMgNi4zMDMxNCA2Ljk0ODk2IDYuMjc0NTEgNi43NTc4MSA2LjI3NDQxQzUuNzAyMTMgNi4yNzQ2IDQuODQ2NjggNy4xMzA4IDQuODQ2NjggOC4xODY1MkM0Ljg0Njg1IDkuMTU0MjggNS41NjU5NSA5Ljk1MDY5IDYuNDk5MDIgMTAuMDc3MUM2LjEzNTEgMTAuNTM0NiA1LjgyMjEyIDExLjAzMzUgNS41NjU0MyAxMS41NjU0QzQuMjU2MTcgMTEuOTk2NSAzLjI1OTE1IDEzLjExMzggMy4wMDI5MyAxNC40OTMyTDIuOTkyMTkgMTQuNTU4Nkg0Ljc5Mjk3QzQuNzg0OTQgMTQuNzA0NyA0Ljc3OTMgMTQuODUxOSA0Ljc3OTMgMTVDNC43NzkzIDE1LjI5NjMgNC44NDI1NiAxNS41Nzc4IDQuOTU0MSAxNS44MzNIMi4zMDA3OEMxLjk0OTAzIDE1LjgzMjggMS42NjQwNiAxNS41NDcxIDEuNjY0MDYgMTUuMTk1M0MxLjY2NDIxIDEzLjEzNjkgMi44ODQyOCAxMS4zNjQ0IDQuNjQwNjIgMTAuNTU5NkMzLjk4NzM5IDkuOTc2MjYgMy41NzMzMSA5LjEzMTIzIDMuNTczMjQgOC4xODY1MkMzLjU3MzI0IDYuNDI2NzkgNS4wMDAwMyA1IDYuNzU5NzcgNVpNMTIuNjkzNCAzLjc1QzExLjA4MjUgMy43NSA5Ljc3NjM3IDUuMDU2MTYgOS43NzYzNyA2LjY2Njk5QzkuNzc2NDUgNy40NzMwNyAxMC4xMDM0IDguMjAxNSAxMC42MzU3IDguNzMxNDVMMTAuNzQ0MSA4LjgzNDk2QzExLjQxODEgOS40NDEyOSAxMS4yNDg2IDEwLjUzNTYgMTAuNDI5NyAxMC45MTMxQzguNjg1NTcgMTEuNzE2OCA3LjQ0NDIzIDEzLjQxNiA3LjI5MTk5IDE1LjQxOEgxOC4wOTQ3QzE3Ljk0MjQgMTMuNDE2NCAxNi43MDA5IDExLjcxNyAxNC45NTcgMTAuOTEzMUMxNC4xMzgzIDEwLjUzNTcgMTMuOTY4MiA5LjQ0MTM1IDE0LjY0MTYgOC44MzQ5NkMxNS4yMzczIDguMjk4ODQgMTUuNjA5MyA3LjUyNjk1IDE1LjYwOTQgNi42NjY5OUMxNS42MDk0IDUuMDU2MjUgMTQuMzAzOCAzLjc1MDM1IDEyLjY5MzQgMy43NVoiIGZpbGw9IiNEMkQyRDIiLz4KPC9zdmc+Cg=='),
  more: asset('PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIj4KPGNpcmNsZSBjeD0iNiIgY3k9IjEyIiByPSIyIiBmaWxsPSIjMkIyQjJCIi8+CjxjaXJjbGUgY3g9IjEzIiBjeT0iMTIiIHI9IjIiIGZpbGw9IiMyQjJCMkIiLz4KPGNpcmNsZSBjeD0iMjAiIGN5PSIxMiIgcj0iMiIgZmlsbD0iIzJCMkIyQjIiLz4KPC9zdmc+'),
}

const styles: Record<string, CSSProperties> = {
  headerActions: ARKME_CONVERSATION_HEADER_ACTIONS_STYLE,
  headerButton: ARKME_CONVERSATION_HEADER_BUTTON_STYLE,
  icon: {
    width: 20, height: 20, display: 'block', backgroundColor: 'currentColor', opacity: .84,
    maskRepeat: 'no-repeat', maskPosition: 'center', maskSize: 'contain',
    WebkitMaskRepeat: 'no-repeat', WebkitMaskPosition: 'center', WebkitMaskSize: 'contain',
  },
  drawer: {
    position: 'absolute', top: 68, right: 0, bottom: 0, zIndex: 8, width: 262, maxWidth: '86%',
    display: 'flex', flexDirection: 'column', background: colors.panel,
    boxShadow: '0 4px 10px rgba(0,0,0,.1)',
  },
  drawerScrim: { position: 'absolute', inset: 0, zIndex: 7, background: 'transparent' },
  closeButton: {
    width: 30, height: 30, border: 0, borderRadius: 4, background: 'transparent',
    color: colors.secondary, display: 'grid', placeItems: 'center', cursor: 'pointer', fontSize: 20,
  },
  drawerBody: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 10px 18px' },
  memberRow: {
    width: '100%', border: 0, background: 'transparent', borderRadius: 8, padding: '12px 6px',
    display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', color: colors.text, cursor: 'pointer',
  },
  avatar: {
    width: 32, height: 32, borderRadius: 999, overflow: 'hidden', flex: 'none',
    display: 'grid', placeItems: 'center', background: colors.subtle, color: colors.secondary,
  },
  avatarImage: { width: '100%', height: '100%', display: 'block', objectFit: 'cover' },
  memberMain: { minWidth: 0, flex: 1 },
  memberNameLine: { minWidth: 0, display: 'flex', alignItems: 'center', gap: 6 },
  memberName: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 14, lineHeight: '20px' },
  badge: { flex: 'none', color: colors.primary, fontSize: 11, lineHeight: '16px' },
  empty: { padding: '38px 18px', color: colors.secondary, fontSize: 13, textAlign: 'center' },
  loading: { padding: '14px 16px', color: colors.secondary, fontSize: 13, textAlign: 'center' },
  restrictionDrawer: {
    position: 'absolute', top: 68, right: 0, bottom: 0, zIndex: 8,
    width: GROUP_JOIN_RESTRICTIONS_PANEL_WIDTH, maxWidth: '92%', overflow: 'hidden',
    display: 'flex', flexDirection: 'column', background: colors.panel,
    borderLeft: `1px solid ${colors.border}`, borderRadius: '12px 0 0 0',
    boxShadow: '-8px 14px 28px rgba(24, 29, 36, .1)',
  },
  restrictionDescription: {
    flex: 'none', margin: 0, padding: '12px 16px', borderBottom: `1px solid ${colors.border}`,
    color: colors.secondary, fontSize: 12, lineHeight: '18px',
  },
  restrictionBody: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 12px 18px' },
  restrictionRow: {
    width: '100%', minHeight: 58, padding: '10px 4px', border: 0,
    borderBottom: `1px solid ${arkmeTheme.borderSoft}`, background: 'transparent',
    boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 10, color: colors.text,
  },
  restrictionStatus: { display: 'block', color: colors.secondary, fontSize: 12, lineHeight: '18px' },
  restrictionAction: {
    minWidth: 82, height: 34, flex: 'none', padding: '0 12px', border: `1px solid ${colors.border}`,
    borderRadius: 9, background: arkmeTheme.elevated, color: colors.text,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', font: 'inherit', fontSize: 13, whiteSpace: 'nowrap',
    transition: 'background-color 120ms ease, opacity 120ms ease',
  },
  restrictionError: {
    margin: '10px 4px 0', padding: '9px 10px', borderRadius: 8,
    display: 'flex', alignItems: 'center', gap: 8, background: arkmeTheme.dangerSoft,
    color: arkmeTheme.danger, fontSize: 12, lineHeight: '18px',
  },
  restrictionRetry: {
    minHeight: 30, flex: 'none', marginLeft: 'auto', padding: '0 8px', border: 0, borderRadius: 7,
    background: 'transparent', color: arkmeTheme.danger, cursor: 'pointer', font: 'inherit', fontWeight: 600,
  },
  restrictionLoadMore: {
    width: '100%', minHeight: 34, marginTop: 8, padding: '0 12px', border: 0, borderRadius: 9,
    background: 'transparent', color: colors.primary, cursor: 'pointer', font: 'inherit', fontSize: 12, fontWeight: 600,
  },
  menuScrim: ARKME_CONVERSATION_SETTINGS_MENU_SCRIM_STYLE,
  aiModalScrim: {
    position: 'absolute', inset: 0, zIndex: 11, padding: 24, display: 'grid', placeItems: 'center',
    background: 'rgba(15, 23, 42, .14)', backdropFilter: 'blur(1px)', boxSizing: 'border-box',
  },
  popover: ARKME_CONVERSATION_SETTINGS_POPOVER_STYLE,
  aiPopover: {
    position: 'relative', width: AI_POLISH_PANEL_WIDTH, maxWidth: '100%', maxHeight: '100%',
    display: 'flex', flexDirection: 'column', overflow: 'hidden', border: `1px solid ${colors.border}`,
    borderRadius: 16, background: colors.panel, boxShadow: '0 20px 60px rgba(20, 24, 31, .20)', boxSizing: 'border-box',
  },
  aiHeader: {
    flex: 'none', height: 56, padding: '0 12px', display: 'grid',
    gridTemplateColumns: '72px minmax(0, 1fr) 72px', alignItems: 'center',
    borderBottom: `1px solid ${colors.border}`, boxSizing: 'border-box',
  },
  aiHeaderButton: {
    width: 32, height: 32, padding: 0, border: 0, borderRadius: 9, background: 'transparent', color: colors.secondary,
    display: 'grid', placeItems: 'center', cursor: 'pointer', justifySelf: 'start',
  },
  aiHeaderTitle: {
    minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    color: colors.text, fontSize: 15, lineHeight: '22px', fontWeight: 600, textAlign: 'center',
  },
  aiApplyButton: {
    width: 72, height: 32, padding: 0, border: 0, borderRadius: 9, justifySelf: 'end',
    background: colors.text, color: arkmeTheme.foreground, cursor: 'pointer', fontSize: 12, lineHeight: '18px', fontWeight: 600,
  },
  aiBody: { minHeight: 0, overflowY: 'auto', padding: '10px 12px 12px' },
  aiRuleRow: {
    width: '100%', minHeight: 42, padding: '7px 8px', border: 0, borderRadius: 4,
    display: 'flex', alignItems: 'center', gap: 9, color: colors.text, background: 'transparent',
    textAlign: 'left', cursor: 'pointer', boxSizing: 'border-box',
  },
  aiRadio: { width: 14, height: 14, border: `1px solid ${colors.secondary}`, borderRadius: 999, flex: 'none', boxSizing: 'border-box' },
  aiRuleName: { minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, lineHeight: '19px' },
  aiRuleStatus: { flex: 'none', color: colors.secondary, fontSize: 11, lineHeight: '16px' },
  aiThread: {
    minHeight: 96, maxHeight: 300, overflowY: 'auto', padding: '18px 18px 8px',
    background: colors.panel, boxSizing: 'border-box',
  },
  aiAssistantMessage: {
    maxWidth: '100%', marginBottom: 14, display: 'flex', alignItems: 'flex-start', gap: 10,
    color: colors.text, fontSize: 13, lineHeight: '20px', whiteSpace: 'pre-wrap',
  },
  aiAssistantIcon: {
    width: 26, height: 26, flex: 'none', display: 'grid', placeItems: 'center', borderRadius: 8,
    background: arkmeTheme.infoSoft, color: colors.primary,
  },
  aiMessage: {
    width: 'fit-content', maxWidth: '92%', marginBottom: 12, padding: '2px 0',
    color: colors.text, background: 'transparent', fontSize: 13, lineHeight: '20px', whiteSpace: 'pre-wrap',
  },
  aiComposer: {
    flex: 'none', padding: '8px 14px 14px', background: colors.panel,
  },
  aiInputShell: {
    position: 'relative', minHeight: 68, overflow: 'hidden', border: `1px solid ${colors.border}`,
    borderRadius: 12, background: colors.panel, boxSizing: 'border-box',
  },
  aiTextarea: {
    width: '100%', minHeight: 66, maxHeight: 120, resize: 'none', border: 0,
    borderRadius: 12, padding: '12px 52px 12px 12px', outline: 0, color: colors.text, background: 'transparent',
    fontSize: 13, lineHeight: '18px', boxSizing: 'border-box',
  },
  aiSendButton: {
    position: 'absolute', right: 8, bottom: 8, width: 34, height: 34, padding: 0, border: 0, borderRadius: 10,
    display: 'grid', placeItems: 'center', color: arkmeTheme.foreground, background: colors.text, cursor: 'pointer',
  },
  aiError: { padding: '7px 10px', color: arkmeTheme.danger, fontSize: 12, lineHeight: '18px' },
  menuRow: ARKME_CONVERSATION_SETTINGS_MENU_ROW_STYLE,
  switch: {
    marginLeft: 'auto', width: 40, height: 22, border: 0, borderRadius: 999, padding: 2,
    display: 'flex', alignItems: 'center', cursor: 'pointer', transition: 'background .15s ease',
  },
  switchThumb: {
    width: 18, height: 18, borderRadius: 999, background: arkmeTheme.foreground, boxShadow: '0 1px 2px rgba(0,0,0,.14)',
    transition: 'transform .15s ease',
  },
  dialogScrim: {
    position: 'absolute', inset: 0, zIndex: 30, display: 'grid', placeItems: 'center',
    background: 'var(--dsw-alias-bg-mask-1, rgba(0,0,0,.18))', padding: 20,
  },
  dialog: {
    width: 420, maxWidth: '100%', padding: 16, borderRadius: 12, background: colors.panel,
    boxShadow: '0 12px 40px rgba(0,0,0,.18)', boxSizing: 'border-box',
  },
  dialogTitle: { margin: '0 0 14px', fontSize: 16, lineHeight: '22px', fontWeight: 600, color: colors.text },
  dialogInput: {
    width: '100%', height: 38, border: `1px solid ${colors.border}`, borderRadius: 8, padding: '0 10px',
    outline: 0, color: colors.text, background: colors.panel, fontSize: 14, boxSizing: 'border-box',
  },
  dialogActions: { marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 },
  dialogButton: { height: 34, border: 0, borderRadius: 8, padding: '0 16px', cursor: 'pointer', fontSize: 14 },
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function roleLabel(member: Pick<ArkmeConversationMemberItem, 'role'>): string {
  if (member.role === 'owner') return tr("发起人")
  if (member.role === 'admin') return '管理员'
  return ''
}

function ClientIcon({ src, size = 20 }: { src: string; size?: number }) {
  return <span aria-hidden style={{
    ...styles.icon, width: size, height: size,
    maskImage: `url("${src}")`, WebkitMaskImage: `url("${src}")`,
  }} />
}

export function ArkmeConversationMoreIcon() {
  return <IconEllipsisOutline16 size={24} />
}

function MagicWandIcon() {
  return <svg aria-hidden width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
    <path d="m15 4 1-2 1 2 2 1-2 1-1 2-1-2-2-1 2-1Z" fill="currentColor" />
    <path d="m6 8 1.2-2.5L8.5 8 11 9.2l-2.5 1.3L7.2 13 6 10.5 3.5 9.2 6 8Z" fill="currentColor" opacity=".72" />
    <path d="m9 18 8.8-8.8a1.4 1.4 0 0 1 2 2L11 20a1.4 1.4 0 0 1-2-2Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
}

export function ArkmeConversationHeaderIconButton(props: {
  label: string
  title?: string
  children: ReactNode
  buttonRef?: RefObject<HTMLButtonElement>
  hasPopup?: boolean | 'dialog'
  expanded?: boolean
  busy?: boolean
  onClick: MouseEventHandler<HTMLButtonElement>
}) {
  return <button data-arkme-feedback="neutral"
    ref={props.buttonRef}
    type="button"
    aria-label={props.label}
    aria-haspopup={props.hasPopup === 'dialog' ? 'dialog' : props.hasPopup ? 'menu' : undefined}
    aria-expanded={props.expanded}
    aria-busy={props.busy || undefined}
    title={props.title ?? props.label}
    style={styles.headerButton}
    onClick={props.onClick}
  >{props.children}</button>
}

function Avatar({ imageRef, size = 32, lazy = false }: { imageRef: string | undefined; size?: number; lazy?: boolean }) {
  useArkmeLocale()
  const container = useRef<HTMLSpanElement>(null)
  const src = useArkmeAvatarImage(imageRef, lazy ? container : undefined) ?? ''
  return <span ref={container} style={{ ...styles.avatar, width: size, height: size }}>
    {src === '' ? <ArkmeMark size={size} /> : <img src={src} alt="" draggable={false} style={styles.avatarImage} />}
  </span>
}

export const GroupMemberRow = memo(function GroupMemberRow({ member, onMemberOpen, onMemberContextMenu, onMemberHover }: {
  member: ArkmeConversationMemberItem
  onMemberOpen: (member: ArkmeConversationMemberItem) => void
  onMemberContextMenu: (member: ArkmeConversationMemberItem, rect: DOMRect) => void
  onMemberHover?: ((member: ArkmeConversationMemberItem, anchor: HTMLElement, side: 'left') => void) | undefined
}) {
  useArkmeLocale()
  const badge = roleLabel(member)
  return <button data-arkme-feedback="neutral"
    type="button"
    style={styles.memberRow}
    data-arkme-group-member-row
    onPointerEnter={event => {
      if (event.pointerType === 'mouse' && event.buttons === 0) onMemberHover?.(member, event.currentTarget, 'left')
    }}
    onKeyDown={event => {
      if (event.key !== 'ArrowDown') return
      event.preventDefault()
      onMemberContextMenu(member, event.currentTarget.getBoundingClientRect())
    }}
    onClick={() => { onMemberOpen(member) }}
    onContextMenu={event => {
      event.preventDefault()
      const avatar = event.currentTarget.querySelector('[data-arkme-member-avatar]')
      onMemberContextMenu(member, (avatar ?? event.currentTarget).getBoundingClientRect())
    }}
  >
    <span data-arkme-member-avatar="true"><Avatar imageRef={member.avatarRef} lazy /></span>
    <span style={styles.memberMain}>
      <span style={styles.memberNameLine}>
        <span style={styles.memberName}>{member.displayName}{member.isSelf ? tr('（我）') : ''}</span>
        {badge !== '' && <span style={styles.badge}>{badge}</span>}
      </span>
      <span style={{ display: 'block', minHeight: 16, fontSize: 12, lineHeight: '16px', color: colors.secondary }}>{member.statsKnown === false ? '' : tr("{v0}条快记", { v0: member.recordCount })}</span>
    </span>
  </button>
})

export function GroupMembersDrawer(props: {
  source: ArkmeSourceItem
  open: boolean
  accountScope: string | undefined
  onClose: () => void
  onAdd: () => void
  onMemberOpen: (member: ArkmeConversationMemberItem) => void
  onMemberContextMenu: (member: ArkmeConversationMemberItem, anchorRect: DOMRect) => void
  onMemberHover?: ((member: ArkmeConversationMemberItem, anchor: HTMLElement, side: 'left') => void) | undefined
  onError: (message: string) => void
}) {
  useArkmeLocale()
  const panelRef = useRef<HTMLElement>(null)
  const resize = useResizableNoteDetail(panelRef, 'arkme:group-members-width:v1', '调整群成员宽度', 262)
  const snapshot = useConversationMembers(props.accountScope, props.source, props.open)
  const loading = snapshot.refreshing
  useEffect(() => {
    if (!props.open) return
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented && !document.querySelector('[role="menu"], [aria-modal="true"]')) props.onClose()
    }
    window.addEventListener('keydown', dismissOnEscape)
    return () => { window.removeEventListener('keydown', dismissOnEscape) }
  }, [props.onClose, props.open])

  if (!props.open) return null
  const visibleSnapshot = snapshot.ready ? snapshot : undefined
  const items = snapshot.items
  return <>
    <div style={styles.drawerScrim} aria-hidden onPointerDown={event => { event.preventDefault(); props.onClose() }} />
    <aside ref={panelRef} style={{ ...styles.drawer, ...resize.style }} aria-label={tr("群成员")}>
    {resize.handle}
    <ArkmeRightPanelHeader title={<>{tr("群成员")}{visibleSnapshot === undefined ? '' : `（${visibleSnapshot.items.length}）`}</>}
      onClose={props.onClose} closeLabel={tr("关闭群成员")}
      actions={<button data-arkme-feedback="neutral" type="button" style={{ ...styles.closeButton, height: 30, marginTop: -3, width: 'auto', padding: '0 6px', fontSize: 14, fontWeight: 700, color: colors.primary }} onClick={props.onAdd}>{tr("添加")}</button>} />
    <div style={styles.drawerBody}>
      {loading && items.length === 0 ? <div style={styles.loading}>{tr("正在读取群成员…")}</div> : null}
      {snapshot.error !== undefined && <button data-arkme-feedback="neutral" type="button" role="alert" style={styles.restrictionRetry}
        onClick={() => { if (props.accountScope !== undefined) void arkmeConversationMembers.ensure(props.accountScope, props.source, true) }}
      >{snapshot.error}{tr("，点击重试")}</button>}
      {snapshot.ready && !loading && snapshot.error === undefined && items.length === 0 ? <div style={styles.empty}>{tr("暂无群成员")}</div> : null}
      {items.map(member => <GroupMemberRow key={member.memberRef} member={member}
        onMemberOpen={props.onMemberOpen} onMemberContextMenu={props.onMemberContextMenu} onMemberHover={props.onMemberHover} />)}
    </div>
    </aside>
  </>
}

function qrDataUrl(content: string): string {
  const qr = qrcode(0, 'M')
  qr.addData(content)
  qr.make()
  return qr.createDataURL(6, 0)
}

function inviteExpireText(expireAtMillis: number): string {
  if (expireAtMillis <= 0) return '二维码有效期24小时'
  const date = new Date(expireAtMillis)
  const pad = (value: number) => String(value).padStart(2, '0')
  const year = date.getFullYear() === new Date().getFullYear() ? '' : `${String(date.getFullYear())}-`
  return `二维码有效期至 ${year}${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function InviteActionIcon({ kind }: { kind: 'copy' | 'save' | 'add' }) {
  if (kind === 'copy') return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M16 12.9V17.1C16 20.6 14.6 22 11.1 22H6.9C3.4 22 2 20.6 2 17.1V12.9C2 9.4 3.4 8 6.9 8H11.1C14.6 8 16 9.4 16 12.9Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M22 6.9V11.1C22 14.6 20.6 16 17.1 16H16V12.9C16 9.4 14.6 8 11.1 8H8V6.9C8 3.4 9.4 2 12.9 2H17.1C20.6 2 22 3.4 22 6.9Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
  if (kind === 'save') return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 8.5V14.5M9 12.5L12 15.5L15 12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path fillRule="evenodd" clipRule="evenodd" d="M12 4.5C10.067 4.5 8.5 6.067 8.5 8C8.5 9.933 10.067 11.5 12 11.5C13.933 11.5 15.5 9.933 15.5 8C15.5 6.067 13.933 4.5 12 4.5ZM7 8C7 5.23858 9.23858 3 12 3C14.7614 3 17 5.23858 17 8C17 9.88174 15.9605 11.5207 14.4244 12.3739C15.1087 12.5913 15.753 12.8982 16.3437 13.2808C15.7833 13.4775 15.2686 13.7712 14.8196 14.1417C13.9667 13.7305 13.0103 13.5 12 13.5C8.57838 13.5 5.77426 16.1438 5.51894 19.5H13.2289C13.4005 20.0464 13.6634 20.5523 13.9996 21H5C4.44772 21 4 20.5523 4 20C4 16.4269 6.34252 13.4009 9.5756 12.3739C8.03951 11.5207 7 9.88174 7 8Z" fill="currentColor" />
    <path d="M18 16V20M20 18H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
}

function InviteSavingSpinner() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeOpacity=".22" strokeWidth="1.8" />
    <path d="M12 3.5A8.5 8.5 0 0 1 20.5 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur=".75s" repeatCount="indefinite" />
    </path>
  </svg>
}

function CloseGlyph() {
  return <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden style={{ display: 'block' }}>
    <path d="M1.0804 2.81662C.59579 2.33201.59579 1.5463 1.0804 1.06169C1.56501.57708 2.35072.57708 2.83533 1.06169L8.14213 6.36849L13.4489 1.06169C13.9335.57708 14.7193.57708 15.2039 1.06169C15.6885 1.5463 15.6885 2.33201 15.2039 2.81662L9.89707 8.12342L15.2225 13.4489C15.7071 13.9335 15.7071 14.7192 15.2225 15.2038C14.7379 15.6884 13.9522 15.6884 13.4676 15.2038L8.14213 9.87835L2.81666 15.2038C2.33205 15.6884 1.54634 15.6884 1.06173 15.2038C.57712 14.7192.57712 13.9335 1.06173 13.4489L6.3872 8.12342L1.0804 2.81662Z" fill="currentColor" />
  </svg>
}

export function AddMembersDrawer(props: {
  source: ArkmeSourceItem
  open: boolean
  onClose: () => void
  onAdded: () => void
  onError: (message: string) => void
}) {
  useArkmeLocale()
  type Focus = 'all' | 'group' | 'privateChat' | 'bot'
  const [query, setQuery] = useState('')
  const [snapshot, setSnapshot] = useState<ArkmeGroupMemberCandidateList>()
  const [groups, setGroups] = useState<ArkmeSourceItem[]>([])
  const [groupCandidates, setGroupCandidates] = useState<Record<string, ArkmeGroupMemberCandidateGroup>>({})
  const [expandedGroups, setExpandedGroups] = useState<string[]>([])
  const [loadingGroups, setLoadingGroups] = useState<string[]>([])
  const [bots, setBots] = useState<ArkmeGroupBotCandidateList>()
  const [focus, setFocus] = useState<Focus>('all')
  const [selected, setSelected] = useState<string[]>([])
  const [selectedBots, setSelectedBots] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [searchingGroups, setSearchingGroups] = useState(false)
  const [busy, setBusy] = useState(false)

  const normalizedQuery = query.trim().toLocaleLowerCase()
  const isSearchMode = normalizedQuery !== ''

  useEffect(() => {
    if (!props.open) {
      setQuery('')
      setSnapshot(undefined)
      setGroups([])
      setGroupCandidates({})
      setExpandedGroups([])
      setLoadingGroups([])
      setBots(undefined)
      setFocus('all')
      setSelected([])
      setSelectedBots([])
      setLoading(false)
      setSearchingGroups(false)
      setBusy(false)
      return
    }
    const controller = new AbortController()
    let cancelled = false
    setQuery('')
    setSnapshot(undefined)
    setGroups([])
    setGroupCandidates({})
    setExpandedGroups([])
    setLoadingGroups([])
    setBots(undefined)
    setFocus('all')
    setSelected([])
    setSelectedBots([])
    setSearchingGroups(false)
    setBusy(false)
    setLoading(true)
    void Promise.allSettled([
      callArkme<ArkmeGroupMemberCandidateList>('group.member-candidates', {
        sourceRef: props.source.sourceRef, query: '', limit: 50,
      }, controller.signal),
      callArkme<ArkmeGroupBotCandidateList>('group.bots', { sourceRef: props.source.sourceRef }, controller.signal),
    ])
      .then(([peopleResult, botResult]) => {
        if (cancelled) return
        if (peopleResult.status === 'fulfilled') {
          setSnapshot(peopleResult.value)
          setGroups(peopleResult.value.groups)
        } else {
          props.onError(errorMessage(peopleResult.reason))
        }
        if (botResult.status === 'fulfilled') setBots(botResult.value)
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true; controller.abort() }
  }, [props.open, props.source.sourceRef])

  const mergeGroupCandidates = (items: ArkmeGroupMemberCandidateGroup[]) => {
    setGroupCandidates(current => {
      const next = { ...current }
      for (const item of items) next[item.group.sourceRef] = item
      return next
    })
  }

  const loadGroupMembers = (group: ArkmeSourceItem) => {
    if (busy || loadingGroups.includes(group.sourceRef)) return
    const controller = new AbortController()
    setLoadingGroups(value => [...value, group.sourceRef])
    void callArkme<ArkmeGroupMemberCandidateList>('group.member-candidates', {
      sourceRef: props.source.sourceRef,
      query,
      limit: 50,
      groupSourceRefs: [group.sourceRef],
    }, controller.signal)
      .then(value => {
        setSnapshot(current => current ?? value)
        if (value.groupCandidates.length > 0) mergeGroupCandidates(value.groupCandidates)
      })
      .catch(caught => {
        setGroupCandidates(current => ({
          ...current,
          [group.sourceRef]: {
            group, items: [], total: 0, error: errorMessage(caught),
          },
        }))
      })
      .finally(() => { setLoadingGroups(value => value.filter(ref => ref !== group.sourceRef)) })
  }

  useEffect(() => {
    if (!props.open || groups.length === 0) return
    for (const group of groups.slice(0, 3)) {
      if (groupCandidates[group.sourceRef] === undefined && !loadingGroups.includes(group.sourceRef)) loadGroupMembers(group)
    }
  }, [props.open, groups])

  useEffect(() => {
    if (!props.open || !isSearchMode || groups.length === 0) return
    const controller = new AbortController()
    const timeout = setTimeout(() => {
      setSearchingGroups(true)
      void callArkme<ArkmeGroupMemberCandidateList>('group.member-candidates', {
        sourceRef: props.source.sourceRef,
        query,
        limit: 50,
        groupSourceRefs: groups.map(group => group.sourceRef),
      }, controller.signal)
        .then(value => {
          setSnapshot(value)
          mergeGroupCandidates(value.groupCandidates)
        })
        .catch(caught => { props.onError(errorMessage(caught)) })
        .finally(() => { setSearchingGroups(false) })
    }, 220)
    return () => { clearTimeout(timeout); controller.abort() }
  }, [props.open, props.source.sourceRef, query, groups])

  if (!props.open) return null

  const people = snapshot?.items ?? []
  const botItems = bots?.items ?? []
  const visibleContacts = people.filter(item => item.relation === 'contact')
  const visiblePrivateChats = people.filter(item => item.relation === 'stranger')
  const allGroupMembers = Object.values(groupCandidates).flatMap(group => group.items)
  const groupMemberByRef = new Map<string, ArkmeGroupMemberCandidate>()
  for (const item of allGroupMembers) {
    if (!groupMemberByRef.has(item.candidateRef)) groupMemberByRef.set(item.candidateRef, item)
  }
  const selectedCandidates = [...people, ...groupMemberByRef.values()].filter(item => selected.includes(item.candidateRef))
  const selectedCandidateByRef = new Map(selectedCandidates.map(item => [item.candidateRef, item]))
  const selectedBotItems = botItems.filter(item => selectedBots.includes(item.botRef))
  const visibleGroups = groups.filter(item => normalizedQuery === '' || item.displayName.toLocaleLowerCase().includes(normalizedQuery)
    || (groupCandidates[item.sourceRef]?.items ?? []).some(candidate => candidate.displayName.toLocaleLowerCase().includes(normalizedQuery)))
  const visibleBots = botItems.filter(item => normalizedQuery === '' || item.name.toLocaleLowerCase().includes(normalizedQuery)
    || item.description.toLocaleLowerCase().includes(normalizedQuery))
  const matchedMembers = [...people, ...groupMemberByRef.values()]
    .filter(item => item.displayName.toLocaleLowerCase().includes(normalizedQuery))
    .filter((item, index, values) => values.findIndex(value => value.candidateRef === item.candidateRef) === index)
  const matchedGroups = groups.filter(item => item.displayName.toLocaleLowerCase().includes(normalizedQuery))
  const matchedBots = visibleBots
  const effectiveFocus: Focus = isSearchMode ? 'all' : focus
  const selectedCount = selectedCandidateByRef.size + selectedBots.length
  const showStrangerSection = !isSearchMode && !loading && (effectiveFocus === 'privateChat' || (effectiveFocus === 'all' && visiblePrivateChats.length > 0))
  const focusRows: Array<{ focus: Exclude<Focus, 'all'>; label: string; count: number }> = [
    { focus: 'group', label: '群聊', count: groups.length },
    { focus: 'privateChat', label: '陌生人', count: snapshot?.strangerCount ?? 0 },
    { focus: 'bot', label: 'Bot', count: botItems.length },
  ]
  const focusIcon = (value: Exclude<Focus, 'all'>) => value === 'group'
    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M3.5 19c.5-3.3 2.5-5 5.5-5s5 1.7 5.5 5M16 6.5a3 3 0 0 1 0 5.5M16.5 14c2.2.3 3.6 1.8 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
    : value === 'privateChat'
      ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M5 5h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-7l-4 3v-3H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.5"/><path d="M8 11h.01M12 11h.01M16 11h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
      : <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 7h12a3 3 0 0 1 3 3v7a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3v-7a3 3 0 0 1 3-3Z" stroke="currentColor" strokeWidth="1.5"/><path d="M12 3v4M8 13h.01M16 13h.01M8 17h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
  const selector = (checked: boolean, disabled = false) => <span aria-hidden style={{
    width: 20, height: 20, borderRadius: 999, flex: 'none', display: 'grid', placeItems: 'center', boxSizing: 'border-box',
    border: `1px solid ${colors.border}`, background: checked ? (disabled ? colors.subtle : arkmeTheme.primaryAction) : 'transparent', color: disabled ? colors.secondary : arkmeTheme.onPrimaryAction, fontSize: 12,
  }}>{checked ? '✓' : ''}</span>
  const sectionTitle = (label: string) => <div style={{ padding: '12px 4px 4px', color: colors.secondary, fontSize: 12 }}>{label}</div>
  const toggleCandidate = (item: ArkmeGroupMemberCandidate) => {
    if (busy || item.disabled === true) return
    setSelected(value => value.includes(item.candidateRef)
      ? value.filter(ref => ref !== item.candidateRef)
      : [...value, item.candidateRef])
  }
  const candidateRow = (item: ArkmeGroupMemberCandidate, compact = false) => {
    const checked = selected.includes(item.candidateRef)
    const disabled = item.disabled === true
    return <button data-arkme-feedback="neutral"
      key={item.candidateRef}
      type="button"
      disabled={busy || disabled}
      aria-pressed={checked}
      style={{ ...styles.memberRow, padding: compact ? '7px 2px' : '8px 2px', borderBottom: `1px solid ${colors.border}`, borderRadius: 0, opacity: disabled ? .55 : 1 }}
      onClick={() => { toggleCandidate(item) }}
    >
      <Avatar imageRef={item.avatarRef} size={compact ? 28 : 32} />
      <span style={styles.memberMain}><span style={styles.memberName}>{item.displayName}</span></span>
      {item.statusText !== undefined && item.statusText !== '' ? <span style={{ color: colors.secondary, fontSize: 11 }}>{item.statusText}</span> : null}
      {selector(checked || item.alreadyMember === true, disabled)}
    </button>
  }
  const botRow = (item: typeof botItems[number]) => {
    const disabled = item.installed || bots?.canAddBots === false
    const checked = item.installed || selectedBots.includes(item.botRef)
    return <button data-arkme-feedback="neutral"
      key={item.botRef}
      type="button"
      disabled={busy || disabled}
      onClick={() => { setSelectedBots(value => checked ? value.filter(ref => ref !== item.botRef) : [...value, item.botRef]) }}
      style={{ ...styles.memberRow, padding: '8px 2px', borderBottom: `1px solid ${colors.border}`, borderRadius: 0, opacity: disabled ? .5 : 1 }}
    >
      <span style={styles.avatar}>🤖</span>
      <span style={styles.memberMain}><span style={styles.memberName}>{item.name}</span></span>
      {item.installed ? <span style={{ color: colors.secondary, fontSize: 11 }}>{tr("已添加")}</span> : null}
      {selector(checked, disabled)}
    </button>
  }
  const toggleGroup = (group: ArkmeSourceItem) => {
    if (busy) return
    const expanded = expandedGroups.includes(group.sourceRef)
    setExpandedGroups(value => expanded ? value.filter(ref => ref !== group.sourceRef) : [...value, group.sourceRef])
    if (!expanded && groupCandidates[group.sourceRef] === undefined) loadGroupMembers(group)
  }
  const groupRow = (group: ArkmeSourceItem) => {
    const expanded = expandedGroups.includes(group.sourceRef)
    const bundle = groupCandidates[group.sourceRef]
    const groupItems = (bundle?.items ?? []).filter(item => normalizedQuery === '' || item.displayName.toLocaleLowerCase().includes(normalizedQuery))
    const isLoading = loadingGroups.includes(group.sourceRef)
    const memberCount = bundle?.total ?? group.groupAvatar?.memberCount ?? group.avatarRefs?.length ?? 0
    return <div key={group.sourceRef}>
      <button data-arkme-feedback="neutral"
        type="button"
        onClick={() => { toggleGroup(group) }}
        style={{ ...styles.memberRow, padding: '8px 2px', borderBottom: expanded ? 0 : `1px solid ${colors.border}`, borderRadius: 0 }}
      >
        <Avatar imageRef={group.avatarRef} />
        <span style={styles.memberMain}><span style={styles.memberName}>{group.displayName}</span></span>
        {memberCount > 0 ? <span style={{ color: colors.secondary, fontSize: 11 }}>{String(memberCount)}{tr("人")}</span> : null}
        <span style={{ color: colors.secondary, fontSize: 22, transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform .12s ease' }}>›</span>
      </button>
      {expanded ? <div style={{ marginLeft: 14, padding: '2px 0 10px 8px', borderLeft: `1px solid rgba(0,0,0,.08)` }}>
        {isLoading ? <div style={{ ...styles.loading, textAlign: 'left' }}>{tr("正在加载群成员")}</div> : null}
        {!isLoading && bundle?.error !== undefined ? <div style={{ padding: '10px 0', display: 'flex', alignItems: 'center', gap: 8, color: colors.secondary, fontSize: 13 }}><span>{bundle.error}</span><button data-arkme-feedback="neutral" type="button" style={{ border: 0, background: 'transparent', color: colors.primary, cursor: 'pointer' }} onClick={() => { loadGroupMembers(group) }}>{tr("重试")}</button></div> : null}
        {!isLoading && bundle?.error === undefined && groupItems.length === 0 ? <div style={{ padding: '10px 0', color: colors.secondary, fontSize: 13 }}>{normalizedQuery === '' ? '暂无可选群成员' : '没有找到相关成员'}</div> : null}
        {groupItems.map(item => candidateRow(item, true))}
      </div> : null}
    </div>
  }
  const selectedPreview = selectedCount === 0
    ? <span style={{ color: colors.secondary, fontSize: 13 }}>{tr("未选择对象")}</span>
    : <span style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
      {[...selectedCandidateByRef.values()].slice(0, 5).map(item => <Avatar key={item.candidateRef} imageRef={item.avatarRef} size={24} />)}
      {selectedBotItems.slice(0, Math.max(0, 5 - selectedCandidateByRef.size)).map(item => <span key={item.botRef} style={{ ...styles.avatar, width: 24, height: 24, fontSize: 13 }}>🤖</span>)}
      {selectedCount > 5 ? <span style={{ minWidth: 30, height: 24, padding: '0 6px', borderRadius: 999, background: colors.subtle, color: colors.secondary, display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 700 }}>+{String(selectedCount - 5)}</span> : null}
    </span>
  const primaryText = snapshot?.mode === 'approval_invite' && selectedCandidateByRef.size > 0 && selectedBots.length === 0 ? '发送邀请' : '确认添加'
  const buttonText = selectedCount > 0 ? `${primaryText}（${String(selectedCount)}）` : primaryText

  return <div style={{ position: 'absolute', inset: 0, zIndex: 35, background: 'rgba(0,0,0,.24)' }} role="presentation" onMouseDown={event => {
    if (event.target === event.currentTarget && !busy) props.onClose()
  }}>
    <section style={{ ...styles.drawer, top: 0, width: 360, maxWidth: '92%', zIndex: 36, background: colors.panel, color: colors.text }} role="dialog" aria-modal="true" aria-label={tr("添加成员")}>
      <ArkmeRightPanelHeader title={tr("添加成员")} onClose={props.onClose} closeLabel={tr("关闭")} />
      <div style={{ position: 'relative', margin: '16px 12px 8px' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ position: 'absolute', left: 12, top: 11, color: colors.secondary }}><circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="2"/><path d="m16 16 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
        <input className="arkme-group-member-search" style={{ ...styles.dialogInput, height: 40, border: 0, borderRadius: 11, paddingLeft: 36, paddingRight: 30, background: arkmeTheme.base }} value={query} placeholder={tr("搜索")} aria-label={tr("搜索成员候选人")} disabled={busy} onChange={event => { setQuery(event.target.value) }} />
        {query !== '' ? <button data-arkme-feedback="neutral" type="button" aria-label={tr("清除搜索")} disabled={busy} onClick={() => { setQuery('') }} style={{ position: 'absolute', right: 8, top: 8, width: 24, height: 24, border: 0, background: 'transparent', color: colors.secondary, cursor: 'pointer' }}><CloseGlyph /></button> : null}
      </div>
      <div style={{ overflowY: 'auto', minHeight: 120, flex: 1, padding: '0 12px' }}>
        {!isSearchMode && <><div style={{ padding: '2px 0 8px', fontSize: 14, fontWeight: 600 }}>{tr("我的会话")}</div>{focusRows.map(row => {
          const active = focus === row.focus
          return <button data-arkme-feedback="neutral" key={row.focus} type="button" onClick={() => { setFocus(current => current === row.focus ? 'all' : row.focus) }} style={{ width: '100%', height: 52, padding: '0 12px', border: 0, borderBottom: `1px solid ${colors.border}`, outline: 0, background: 'transparent', display: 'flex', alignItems: 'center', gap: 12, color: active ? colors.text : colors.secondary, position: 'relative', cursor: 'pointer' }}>
            {active && <span style={{ position: 'absolute', left: 0, width: 2, height: 16, borderRadius: 2, background: colors.text }} />}{focusIcon(row.focus)}<strong style={{ fontSize: 14, fontWeight: active ? 700 : 500 }}>{row.label}</strong><span style={{ marginLeft: 'auto', fontSize: 12 }}>{row.count}</span>
          </button>
        })}</>}
        {loading ? <div style={styles.loading}>{tr("正在读取可添加对象…")}</div> : null}
        {isSearchMode && !loading ? <>
          {searchingGroups && matchedMembers.length + matchedGroups.length + matchedBots.length === 0 ? <div style={styles.loading}>{tr("正在搜索群成员…")}</div> : null}
          {matchedMembers.length > 0 ? <>{(matchedGroups.length + matchedBots.length > 0) && sectionTitle('成员')}{matchedMembers.map(item => candidateRow(item))}</> : null}
          {matchedGroups.length > 0 ? <>{(matchedMembers.length + matchedBots.length > 0) && sectionTitle('群聊')}{matchedGroups.map(groupRow)}</> : null}
          {matchedBots.length > 0 ? <>{(matchedMembers.length + matchedGroups.length > 0) && sectionTitle('Bot')}{matchedBots.map(botRow)}</> : null}
          {!searchingGroups && matchedMembers.length + matchedGroups.length + matchedBots.length === 0 ? <div style={styles.empty}>{tr("没有找到相关对象")}</div> : null}
        </> : null}
        {!isSearchMode && !loading && effectiveFocus === 'all' && visibleContacts.length > 0 ? <>{sectionTitle('联系人')}{visibleContacts.map(item => candidateRow(item))}</> : null}
        {showStrangerSection ? <>{sectionTitle('陌生人')}{visiblePrivateChats.length === 0 ? <div style={{ padding: '10px 0', color: colors.secondary, fontSize: 13 }}>{tr("暂无陌生人")}</div> : visiblePrivateChats.map(item => candidateRow(item))}</> : null}
        {!isSearchMode && !loading && (effectiveFocus === 'all' || effectiveFocus === 'group') ? <>{sectionTitle('群聊')}{visibleGroups.length === 0 ? <div style={{ padding: '10px 0', color: colors.secondary, fontSize: 13 }}>{tr("暂无群聊")}</div> : visibleGroups.map(groupRow)}</> : null}
        {!isSearchMode && !loading && (effectiveFocus === 'all' || effectiveFocus === 'bot') ? <>{sectionTitle('Bot')}{visibleBots.length === 0 ? <div style={{ padding: '10px 0', color: colors.secondary, fontSize: 13 }}>{tr("当前没有可添加到本群的 Bot")}</div> : visibleBots.map(botRow)}</> : null}
      </div>
      <div style={{ height: 58, flex: 'none', display: 'flex', alignItems: 'center', padding: '0 14px', borderTop: `1px solid ${colors.border}`, background: colors.panel }}>
        <span style={{ minWidth: 0, flex: 1 }}>{selectedPreview}</span><span style={{ width: 10 }} />
        <button data-arkme-feedback="neutral"
          type="button"
          style={{ ...styles.dialogButton, width: 126, height: 38, borderRadius: 11, background: selectedCount === 0 ? colors.subtle : arkmeTheme.primaryAction, color: selectedCount === 0 ? colors.secondary : arkmeTheme.onPrimaryAction, opacity: busy ? .55 : 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          disabled={busy || selectedCount === 0}
          onClick={() => {
            setBusy(true)
            const memberRefs = [...selectedCandidateByRef.keys()]
            const memberWrite = memberRefs.length === 0 ? Promise.resolve(undefined) : callArkme<ArkmeGroupMemberAddResult>('group.members.add', { sourceRef: props.source.sourceRef, candidateRefs: memberRefs })
            const botWrites = selectedBots.map(botRef => callArkme('group.bot.add', { sourceRef: props.source.sourceRef, botRef }))
            void Promise.all([memberWrite, ...botWrites])
              .then(([result]) => {
                if (result !== undefined && result.failedCount > 0) props.onError(`已完成 ${String(result.succeededCount)} 人，${String(result.failedCount)} 人失败`)
                props.onAdded()
                props.onClose()
              })
              .catch(caught => { props.onError(errorMessage(caught)) })
              .finally(() => { setBusy(false) })
          }}
        >{busy ? tr("处理中…") : buttonText}</button>
      </div>
    </section>
  </div>
}

export function InviteCollaboratorsDialog(props: {
  source: ArkmeSourceItem
  open: boolean
  onClose: () => void
  onAddMembers: () => void
  onError: (message: string) => void
}) {
  useArkmeLocale()
  const [preview, setPreview] = useState<ArkmeGroupInvitePreview>()
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const [saving, setSaving] = useState(false)
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => {
    if (!props.open) return
    const controller = new AbortController()
    setLoading(true)
    void callArkme<ArkmeGroupInvitePreview>('group.invite-preview', { sourceRef: props.source.sourceRef }, controller.signal)
      .then(setPreview).catch(caught => { props.onError(errorMessage(caught)) }).finally(() => { setLoading(false) })
    return () => { controller.abort() }
  }, [props.open, props.source.sourceRef])
  useEffect(() => () => {
    if (copiedTimerRef.current !== undefined) clearTimeout(copiedTimerRef.current)
  }, [])
  if (!props.open) return null
  const qrUrl = preview === undefined ? '' : qrDataUrl(preview.inviteLink)
  const action = (kind: 'copy' | 'save' | 'add', label: string, onClick: () => void, disabled = false, busy = false) => <button data-arkme-feedback="neutral"
    type="button" disabled={disabled} onClick={onClick}
    aria-busy={busy || undefined}
    style={{ width: 78, padding: 0, border: 0, background: 'transparent', color: colors.secondary, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? .45 : 1 }}
  ><span style={{ width: 48, height: 48, margin: '0 auto 8px', borderRadius: 999, display: 'grid', placeItems: 'center', background: colors.subtle }}>{busy ? <InviteSavingSpinner /> : <InviteActionIcon kind={kind} />}</span><span style={{ fontSize: 12, lineHeight: '18px' }}>{label}</span></button>
  const copyLink = () => {
    if (preview === undefined) return
    void navigator.clipboard.writeText(preview.inviteLink)
      .then(() => {
        if (copiedTimerRef.current !== undefined) clearTimeout(copiedTimerRef.current)
        setCopied(true)
        copiedTimerRef.current = setTimeout(() => { setCopied(false) }, 1800)
      })
      .catch(caught => { props.onError(errorMessage(caught)) })
  }
  const saveQr = () => {
    if (qrUrl === '' || saving) return
    setSaving(true)
    void fetch(qrUrl)
      .then(response => response.blob())
      .then(blob => {
        const objectUrl = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = objectUrl
        anchor.download = `arkme_invite_${String(Date.now())}.png`
        anchor.click()
        URL.revokeObjectURL(objectUrl)
      })
      .catch(caught => { props.onError(errorMessage(caught)) })
      .finally(() => { setSaving(false) })
  }
  return <div style={styles.dialogScrim} role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) props.onClose() }}>
    <section style={{ ...styles.dialog, width: 420 }} role="dialog" aria-modal="true" aria-label={tr("邀请协作者")}>
      <div style={{ display: 'flex', alignItems: 'center' }}><h3 style={{ ...styles.dialogTitle, margin: 0, fontSize: 18 }}>{tr("邀请协作者")}</h3><span style={{ flex: 1 }} /><button data-arkme-feedback="neutral" type="button" aria-label={tr("关闭")} style={{ ...styles.closeButton, width: 28, height: 28, padding: 0, borderRadius: 999, background: colors.subtle, color: colors.secondary }} onClick={props.onClose}><CloseGlyph /></button></div>
      <div style={{ height: 10 }} />
      <div style={{ width: 264, margin: '0 auto', padding: '16px 16px 14px', borderRadius: 18, background: colors.panel, boxSizing: 'border-box', boxShadow: '0 10px 22px rgba(0,0,0,.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}><Avatar imageRef={props.source.avatarRef} size={32} /><div style={{ marginLeft: 10, minWidth: 0 }}><div style={{ fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{preview?.title ?? props.source.displayName}</div><div style={{ marginTop: 2, fontSize: 12, color: colors.secondary }}>{tr("发起人：")}{preview?.inviterDisplayName ?? 'Arkme'}</div></div></div>
        <div style={{ position: 'relative', width: 176, height: 176, padding: 10, margin: '14px auto 0', border: `1px solid ${colors.border}`, borderRadius: 12, boxSizing: 'border-box', background: '#fff', display: 'grid', placeItems: 'center' }}>{loading ? <span style={{ ...styles.loading, color: '#51565f' }}>{tr("加载中…")}</span> : qrUrl === '' ? <span style={{ fontSize: 12, color: '#51565f' }}>{tr("邀请链接生成失败，请重试")}</span> : <img src={qrUrl} alt={tr("群聊邀请二维码")} style={{ width: 156, height: 156, imageRendering: 'pixelated' }} />}{copied ? <span role="status" aria-live="polite" style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', padding: '9px 12px', borderRadius: 4, background: '#fff', boxShadow: '0 3px 12px rgba(0,0,0,.18)', color: '#17191c', fontSize: 12, lineHeight: '18px', whiteSpace: 'nowrap' }}>{tr("邀请链接已复制")}</span> : null}</div>
        <div style={{ marginTop: 14, textAlign: 'center', fontSize: 11.5, color: colors.secondary }}>{inviteExpireText(preview?.expireAtMillis ?? 0)}</div>
      </div>
      <div style={{ marginTop: 10, textAlign: 'center', fontSize: 12, color: colors.secondary }}>{tr("扫码加入，或转发邀请链接")}</div>
      <div style={{ width: 266, margin: '14px auto 0', display: 'flex', justifyContent: 'space-between' }}>{action('copy', '复制链接', copyLink, preview === undefined)}{action('save', '保存', saveQr, qrUrl === '' || saving, saving)}{action('add', '添加成员', () => { props.onClose(); props.onAddMembers() }, loading)}</div>
    </section>
  </div>
}

function MessageDndSwitch(props: { checked: boolean; busy: boolean; onChange: (checked: boolean) => void }) {
  return <span
    role="switch"
    aria-label={tr("消息免打扰")}
    aria-checked={props.checked}
    aria-disabled={props.busy || undefined}
    style={{
      ...styles.switch,
      background: props.checked ? colors.primary : arkmeTheme.active,
      opacity: props.busy ? .55 : 1,
      justifyContent: 'flex-start',
    }}
    onClick={(event?: { stopPropagation(): void }) => {
      event?.stopPropagation()
      if (!props.busy) props.onChange(!props.checked)
    }}
  >
    <span style={{ ...styles.switchThumb, transform: props.checked ? 'translateX(18px)' : 'translateX(0)' }} />
  </span>
}

const initialAiPolishMessage: ArkmeGroupAiPolishThreadMessage = {
  id: 'intro', role: 'ai', text: '告诉我这群快记的润色要求，我会整理成规则。',
}

function GroupAiPolishPanel(props: {
  source: ArkmeSourceItem
  open: boolean
  initialSettings?: ArkmeGroupAiPolishSnapshot | undefined
  onClose: () => void
  onSettingsChanged: (settings: ArkmeGroupAiPolishSnapshot) => void
  onError: (message: string) => void
}) {
  useArkmeLocale()
  const [settings, setSettings] = useState<ArkmeGroupAiPolishSnapshot | undefined>(props.initialSettings)
  const [view, setView] = useState<'rules' | 'editor'>('rules')
  const [editingRule, setEditingRule] = useState<ArkmeGroupAiPolishRule>()
  const [messages, setMessages] = useState<ArkmeGroupAiPolishThreadMessage[]>([initialAiPolishMessage])
  const [input, setInput] = useState('')
  const [candidate, setCandidate] = useState<ArkmeGroupAiPolishRuleCandidate>()
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const messageSequence = useRef(1)

  const refreshSettings = useCallback(async (signal?: AbortSignal) => {
    const next = await retryArkmeRead(() => callArkme<ArkmeGroupAiPolishSnapshot>('source.ai-polish.settings', {
      sourceRef: props.source.sourceRef,
    }, signal), signal === undefined ? {} : { signal })
    setSettings(next)
    props.onSettingsChanged(next)
    return next
  }, [props.onSettingsChanged, props.source.sourceRef])

  useEffect(() => {
    if (!props.open) return
    const controller = new AbortController()
    setSettings(props.initialSettings)
    setView('rules')
    setEditingRule(undefined)
    setMessages([initialAiPolishMessage])
    setCandidate(undefined)
    setInput('')
    setError('')
    setLoading(true)
    void refreshSettings(controller.signal)
      .catch(caught => { if (!isArkmeRequestAbort(caught, controller.signal)) setError(errorMessage(caught)) })
      .finally(() => { setLoading(false) })
    return () => { controller.abort() }
  }, [props.open, props.source.sourceRef])

  useEffect(() => {
    if (!props.open) return
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) props.onClose() }
    window.addEventListener('keydown', onKeyDown)
    return () => { window.removeEventListener('keydown', onKeyDown) }
  }, [busy, props.onClose, props.open])

  const openEditor = (rule?: ArkmeGroupAiPolishRule) => {
    setEditingRule(rule)
    setCandidate(undefined)
    setInput('')
    setError('')
    setMessages(rule === undefined ? [initialAiPolishMessage]
      : rule.threadMessages !== undefined && rule.threadMessages.length > 0 ? rule.threadMessages : [
        initialAiPolishMessage,
        { id: `saved-${rule.ruleRef}`, role: 'ai', text: rule.ruleText, isRule: true, ruleRef: rule.ruleRef },
      ])
    setView('editor')
  }

  const generateRule = async () => {
    const requirement = input.trim()
    if (requirement === '' || busy || settings?.canManage !== true) return
    const currentRuleText = candidate?.ruleText.trim() || editingRule?.ruleText.trim() || ''
    const instruction = currentRuleText === ''
      ? requirement
      : `基于当前规则继续修改。\n当前规则：${currentRuleText}\n本次要求：${requirement}`
    const userMessage: ArkmeGroupAiPolishThreadMessage = {
      id: `user-${String(messageSequence.current++)}`, role: 'user', text: requirement,
    }
    setMessages(current => [...current, userMessage])
    setInput('')
    setBusy(true)
    setError('')
    try {
      const threadMessages = [...messages, userMessage]
      const generated = await callArkme<ArkmeGroupAiPolishRuleCandidate>('source.ai-polish.generate-rule', {
        sourceRef: props.source.sourceRef,
        requirement: instruction,
        threadMessages,
        ...(editingRule === undefined ? {} : { targetRuleRef: editingRule.ruleRef }),
      })
      setCandidate(generated)
      setMessages(generated.threadMessages ?? [...threadMessages, {
        id: `candidate-${String(messageSequence.current++)}`, role: 'ai', text: generated.ruleText, isRule: true,
      }])
    } catch (caught) {
      const message = errorMessage(caught)
      setError(message)
      props.onError(message)
    } finally {
      setBusy(false)
    }
  }

  const applyRule = async () => {
    if (busy || settings?.canManage !== true) return
    setBusy(true)
    setError('')
    try {
      let preview = candidate
      if (preview === undefined && editingRule !== undefined) {
        preview = await callArkme<ArkmeGroupAiPolishRuleCandidate>('source.ai-polish.prepare-enable', {
          sourceRef: props.source.sourceRef,
          ruleRef: editingRule.ruleRef,
        })
      }
      if (preview === undefined) return
      await callArkme<ArkmeGroupAiPolishMutationResult>('source.ai-polish.confirm-enable', {
        confirmationRef: preview.confirmationRef,
      })
      await refreshSettings()
      props.onClose()
    } catch (caught) {
      const message = errorMessage(caught)
      setError(message)
      props.onError(message)
    } finally {
      setBusy(false)
    }
  }

  const disablePolish = async () => {
    if (busy || settings?.canManage !== true) return
    if (settings.enabled !== true) { props.onClose(); return }
    setBusy(true)
    setError('')
    try {
      const preview = await callArkme<ArkmeGroupAiPolishRuleCandidate>('source.ai-polish.prepare-disable', {
        sourceRef: props.source.sourceRef,
      })
      await callArkme<ArkmeGroupAiPolishMutationResult>('source.ai-polish.confirm-disable', {
        confirmationRef: preview.confirmationRef,
      })
      await refreshSettings()
      props.onClose()
    } catch (caught) {
      const message = errorMessage(caught)
      setError(message)
      props.onError(message)
    } finally {
      setBusy(false)
    }
  }

  if (!props.open) return null
  const canManage = settings?.canManage === true
  const canApply = canManage && !busy && (candidate !== undefined || (editingRule !== undefined && !editingRule.isActive))
  const title = editingRule?.name ?? '新建规则'
  return <div style={styles.aiModalScrim} role="presentation" data-arkme-ai-polish-modal-scrim="true" onMouseDown={event => {
    if (event.target === event.currentTarget && !busy) props.onClose()
  }}>
    <section
      style={styles.aiPopover}
      role="dialog"
      aria-label={tr("AI 表达润色")}
      onMouseDown={event => { event.stopPropagation() }}
      aria-modal="true"
    >
      <div style={styles.aiHeader}>
        {view === 'editor' ? <button data-arkme-feedback="neutral" type="button" aria-label={tr("返回规则列表")} style={styles.aiHeaderButton} disabled={busy} onClick={() => { setView('rules'); setError('') }}><ArrowLeft size={18} /></button> : <span />}
        <div style={styles.aiHeaderTitle}>{view === 'rules' ? tr("AI 表达润色") : title}</div>
        {view === 'editor' ? <button data-arkme-feedback="primary"
          type="button"
          data-arkme-ai-polish-apply="true"
          style={{ ...styles.aiApplyButton, opacity: canApply ? 1 : .32, cursor: canApply ? 'pointer' : 'default' }}
          disabled={!canApply}
          onClick={() => { void applyRule() }}
        >{busy ? tr("处理中") : '应用规则'}</button> : <button data-arkme-feedback="neutral" type="button" aria-label={tr("关闭 AI 润色设置")} style={{ ...styles.aiHeaderButton, justifySelf: 'end' }} onClick={props.onClose}><X size={18} /></button>}
      </div>
      {view === 'rules' ? <div style={styles.aiBody}>
        {loading && settings === undefined ? <div style={styles.loading}>{tr("正在读取 AI 润色设置…")}</div> : null}
        {!loading && settings !== undefined ? <>
          <button data-arkme-feedback="neutral"
            type="button"
            style={{ ...styles.aiRuleRow, opacity: canManage ? 1 : .5 }}
            disabled={!canManage || busy}
            onClick={() => { void disablePolish() }}
          ><span style={{ ...styles.aiRadio, ...(!settings.enabled ? { border: `4px solid ${colors.primary}` } : {}) }} /><span style={styles.aiRuleName}>{tr("不润色")}</span></button>
          {settings.rules.map(rule => <button data-arkme-feedback="neutral"
            key={rule.ruleRef}
            type="button"
            data-arkme-ai-polish-rule-ref={rule.ruleRef}
            style={{ ...styles.aiRuleRow, opacity: canManage ? 1 : .5 }}
            disabled={!canManage || busy}
            onClick={() => { openEditor(rule) }}
          ><span style={{ ...styles.aiRadio, ...(rule.isActive ? { border: `4px solid ${colors.primary}` } : {}) }} /><span style={styles.aiRuleName}>{rule.name}</span><span style={styles.aiRuleStatus}>{rule.isActive ? '当前应用' : '›'}</span></button>)}
          <button data-arkme-feedback="neutral"
            type="button"
            data-arkme-ai-polish-new-rule="true"
            style={{ ...styles.aiRuleRow, opacity: canManage ? 1 : .5 }}
            disabled={!canManage || busy}
            onClick={() => { openEditor() }}
          ><Plus size={16} /><span style={styles.aiRuleName}>{tr("新建规则")}</span><CaretRight size={15} color={colors.secondary} /></button>
        </> : null}
        {settings !== undefined && !canManage ? <div style={styles.aiError}>{tr("服务端未授予当前账号该群的 AI 润色设置权限")}</div> : null}
      </div> : <>
        <div style={styles.aiThread} aria-live="polite" data-arkme-ai-polish-thread="true">
          {messages.map(message => message.role === 'ai' && message.isRule !== true ? <div key={message.id} style={styles.aiAssistantMessage}>
            <span style={styles.aiAssistantIcon}><Sparkle size={14} weight="fill" /></span>
            <span style={{ paddingTop: 3 }}>{message.text}</span>
          </div> : <div key={message.id} style={{
            ...styles.aiMessage,
            ...(message.isRule ? {
              width: '100%', maxWidth: '100%', padding: '11px 12px', border: `1px solid ${colors.border}`, borderRadius: 10,
              background: arkmeTheme.subtle, boxSizing: 'border-box',
            } : {}),
            ...(message.role === 'user' ? {
              marginLeft: 'auto', padding: '8px 11px', borderRadius: 10, background: arkmeTheme.infoSoft,
            } : {}),
          }}>
            {message.isRule ? <div style={{ marginBottom: 3, color: colors.secondary, fontSize: 10 }}>{editingRule?.isActive === true && candidate === undefined ? '当前规则' : '规则候选'}</div> : null}
            {message.text}
          </div>)}
          {busy && candidate === undefined ? <div style={{ color: colors.secondary, fontSize: 12 }}>{tr("正在整理规则…")}</div> : null}
        </div>
        <div style={styles.aiComposer}>
          <div style={styles.aiInputShell}>
            <textarea
              style={styles.aiTextarea}
              value={input}
              maxLength={2_000}
              aria-label={tr("润色规则描述")}
              placeholder={tr("例如：语气更轻松，保留原意，不要使用网络热梗")}
              disabled={!canManage || busy}
              onChange={event => { setInput(event.target.value) }}
              onKeyDown={event => {
                if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); void generateRule() }
              }}
            />
            <button data-arkme-feedback="primary"
              type="button"
              aria-label={tr("发送规则描述")}
              title={tr("发送")}
              data-arkme-ai-polish-send="true"
              style={{ ...styles.aiSendButton, opacity: input.trim() === '' || busy ? .28 : 1 }}
              disabled={input.trim() === '' || busy || !canManage}
              onClick={() => { void generateRule() }}
            ><ArrowUp size={16} weight="bold" /></button>
          </div>
        </div>
      </>}
      {error !== '' ? <div role="alert" style={styles.aiError}>{error}</div> : null}
    </section>
  </div>
}

function GroupSettingsMenu(props: {
  source: ArkmeSourceItem
  open: boolean
  buttonRef: RefObject<HTMLButtonElement>
  onOpenChange: (open: boolean) => void
  onClose: () => void
  onRename: (target: ArkmeGroupActionTarget) => void
  onSelfNickname: () => void
  aiPolishSettings?: ArkmeGroupAiPolishSnapshot | undefined
  onAiPolishSettingsChanged: (settings: ArkmeGroupAiPolishSnapshot) => void
  onAiPolishOpen: () => void
  onRestrictionsOpen: () => void
  onSourceProjectionUpdated: (source: ArkmeSourceItem) => void
  onMembershipChanged: (target: ArkmeGroupActionTarget) => void
  onMessageDndUpdated: (target: ArkmeGroupActionTarget, result: ArkmeGroupNotificationResult) => boolean
  onError: (message: string) => void
  onExport: () => void
  exportBusy: boolean
  exportProcessed: number
}) {
  useArkmeLocale()
  const [snapshot, setSnapshot] = useState<ArkmeGroupSettingsSnapshot>()
  const [managementOpen, setManagementOpen] = useState(false)
  const managementFocusRef = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    managementFocusRef.current?.closest('button')?.focus({ preventScroll: true })
  }, [managementOpen])
  const [notification, setNotification] = useState<ArkmeGroupNotificationResult>({
    messageDnd: props.source.isMuted === true,
    chatNotificationPolicyUpdatedAtMillis: props.source.chatNotificationPolicyUpdatedAtMillis ?? 0,
  })
  const messageDnd = (props.source.chatNotificationPolicyUpdatedAtMillis ?? 0) > notification.chatNotificationPolicyUpdatedAtMillis
    ? props.source.isMuted === true : notification.messageDnd
  const [busy, setBusy] = useState(false)
  const [localAiPolishSettings, setLocalAiPolishSettings] = useState(props.aiPolishSettings)
  const [aiPolishLoadState, setAiPolishLoadState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')

  useEffect(() => {
    if (!props.open) return
    const controller = new AbortController()
    let active = true
    void callArkme<ArkmeGroupSettingsSnapshot>('group.settings', {
      sourceRef: props.source.sourceRef,
    }, controller.signal)
      .then(value => {
        if (!active) return
        setSnapshot(value)
        setNotification(current => current.chatNotificationPolicyUpdatedAtMillis > (value.chatNotificationPolicyUpdatedAtMillis ?? 0)
          ? current : { messageDnd: value.messageDnd, chatNotificationPolicyUpdatedAtMillis: value.chatNotificationPolicyUpdatedAtMillis ?? 0 })
      })
      .catch(caught => {
        if (active && !isArkmeRequestAbort(caught, controller.signal)) {
          setSnapshot(undefined)
          props.onError(errorMessage(caught))
        }
      })
    return () => {
      active = false
      controller.abort()
    }
  }, [props.open, props.source.sourceRef])

  useEffect(() => {
    if (props.open) return
    setManagementOpen(false)
    setNotification({
      messageDnd: props.source.isMuted === true,
      chatNotificationPolicyUpdatedAtMillis: props.source.chatNotificationPolicyUpdatedAtMillis ?? 0,
    })
  }, [props.open, props.source.isMuted, props.source.chatNotificationPolicyUpdatedAtMillis])

  useEffect(() => {
    if (props.aiPolishSettings !== undefined) setLocalAiPolishSettings(props.aiPolishSettings)
  }, [props.aiPolishSettings])

  useEffect(() => {
    if (!props.open) return
    const controller = new AbortController()
    let active = true
    let timedOut = false
    setLocalAiPolishSettings(props.aiPolishSettings)
    setAiPolishLoadState('loading')
    const timeout = globalThis.setTimeout(() => {
      timedOut = true
      controller.abort()
    }, 12_000)
    void retryArkmeRead(() => callArkme<ArkmeGroupAiPolishSnapshot>('source.ai-polish.settings', {
      sourceRef: props.source.sourceRef,
    }, controller.signal), { signal: controller.signal })
      .then(value => {
        if (!active) return
        setLocalAiPolishSettings(value)
        setAiPolishLoadState('ready')
        props.onAiPolishSettingsChanged(value)
      })
      .catch(caught => {
        if (!active || (controller.signal.aborted && !timedOut)) return
        setAiPolishLoadState('error')
        props.onError(timedOut ? 'AI 润色设置读取超时，请重试' : errorMessage(caught))
      })
      .finally(() => { globalThis.clearTimeout(timeout) })
    return () => {
      active = false
      globalThis.clearTimeout(timeout)
      controller.abort()
    }
  }, [props.open, props.source.sourceRef])

  const effective = snapshot ?? {
    target: {
      sourceRef: props.source.sourceRef,
      ...(props.source.sourceKey === undefined ? {} : { sourceKey: props.source.sourceKey }),
      kind: 'group_chat',
      displayName: props.source.displayName,
    },
    selfRole: 'unknown' as const,
    selfStatus: 'unknown' as const,
    canRename: false,
    canDissolve: false,
    canLeave: false,
    messageDnd,
  }
  const actionTarget: ArkmeGroupActionTarget = {
    sourceRef: effective.target.sourceRef,
    ...(effective.target.sourceKey === undefined ? {} : { sourceKey: effective.target.sourceKey }),
    kind: effective.target.kind,
    displayName: effective.target.displayName,
  }

  const leaveOrDissolve = useCallback(async () => {
    const operation = effective.canDissolve ? 'group.dissolve' : 'group.leave'
    const prompt = effective.canDissolve ? '确定要解散群聊吗？' : '确定要退出群聊吗？'
    if (!window.confirm(prompt)) return
    setBusy(true)
    try {
      await callArkme<ArkmeGroupCommandResult>(operation, { sourceRef: actionTarget.sourceRef })
      props.onMembershipChanged(actionTarget)
      props.onClose()
    } catch (caught) {
      props.onError(errorMessage(caught))
    } finally {
      setBusy(false)
    }
  }, [actionTarget.sourceRef, effective.canDissolve, props.onClose, props.onError, props.onMembershipChanged])

  const changeMessageDnd = (next: boolean) => {
    setBusy(true)
    void callArkme<ArkmeGroupNotificationResult>('group.notification.set', {
      sourceRef: actionTarget.sourceRef,
      enabled: next,
    }).then(result => {
      const accepted = props.onMessageDndUpdated(actionTarget, result)
      setNotification(current => current.chatNotificationPolicyUpdatedAtMillis > result.chatNotificationPolicyUpdatedAtMillis
        ? current : { ...result, messageDnd: accepted })
    }).catch(caught => { props.onError(errorMessage(caught)) })
      .finally(() => { setBusy(false) })
  }
  const entries: MenuEntry[] = [
    { type: 'label', id: 'personal-label', text: '个人设置' },
    { id: 'self-nickname', label: '修改群昵称', icon: <ClientIcon src={icons.selfNickname} size={16} /> },
  ]
  entries.push({
    id: 'message-dnd',
    label: <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ flex: 1 }}>{tr("消息免打扰")}</span>
      <MessageDndSwitch checked={messageDnd} busy={busy} onChange={changeMessageDnd} />
    </span>,
    icon: <ClientIcon src={icons.notice} size={16} />,
    disabled: busy,
  })
  const polishStatus = localAiPolishSettings === undefined
    ? aiPolishLoadState === 'error' ? '加载失败' : '读取中'
    : localAiPolishSettings.enabled ? localAiPolishSettings.activeRuleName || '已开启' : '不润色'
  entries.push({
    id: 'ai-polish',
    label: <span
      data-arkme-group-ai-polish-entry="true"
      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
      onClick={(event?: { stopPropagation(): void }) => {
        event?.stopPropagation()
        close()
        props.onAiPolishOpen()
      }}
    ><span style={{ flexShrink: 0 }}>{tr("AI 表达润色")}</span><span style={{ marginLeft: 'auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: colors.secondary, fontSize: 13 }}>{polishStatus}</span><CaretRight size={12} color={colors.secondary} style={{ flexShrink: 0 }} aria-hidden /></span>,
    icon: <MagicWandIcon />,
  })
  const canManage = effective.selfRole === 'owner' && effective.selfStatus === 'active'
  entries.push(
    { type: 'separator', id: 'management-separator' },
    { id: 'management', label: <span ref={managementFocusRef} style={{ display: 'flex', alignItems: 'center' }}>{tr('群管理')}<CaretRight size={12} color={colors.secondary} style={{ marginLeft: 'auto' }} aria-hidden /></span>,
      icon: <ClientIcon src={icons.rename} size={16} />, disabled: !effective.canRename && !canManage },
  )
  const managementEntries: MenuEntry[] = [
    { id: 'back', label: <span ref={managementFocusRef}>{tr('返回群聊设置')}</span>, icon: <CaretRight size={16} style={{ transform: 'rotate(180deg)' }} aria-hidden /> },
    { type: 'separator', id: 'management-separator' },
    { id: 'rename', label: '修改群名称', icon: <ClientIcon src={icons.rename} size={16} />, disabled: !effective.canRename },
    { id: 'restrictions', label: '禁止加入名单', icon: <Prohibit size={16} aria-hidden />, disabled: !canManage },
  ]
  entries.push(
    { type: 'separator', id: 'export-separator' },
    { type: 'label', id: 'export-label', text: '聊天记录' },
    { id: 'export', label: props.exportBusy
      ? `正在导出${props.exportProcessed > 0 ? ` · ${String(props.exportProcessed)} 条` : ''}`
      : '导出', icon: <DownloadSimple size={20} weight="regular" aria-hidden />, disabled: props.exportBusy },
    { type: 'separator', id: 'leave-separator' },
    {
      id: 'leave', danger: true,
      label: <span style={{ color: arkmeTheme.danger }}>{effective.canDissolve ? '解散群聊' : tr("退出群聊")}</span>,
      icon: <span style={{ color: arkmeTheme.danger }}><ClientIcon src={icons.exit} size={16} /></span>,
      disabled: busy || (!effective.canLeave && !effective.canDissolve),
    },
  )

  const close = () => {
    props.onOpenChange(false)
    props.onClose()
  }
  return <ArkmeDshMenu
    open={props.open}
    label={tr("群聊设置")}
    conversationAppearance
    align="end"
    portal
    items={managementOpen ? managementEntries : entries}
    onClose={close}
    onSelect={id => {
      if (id === 'management') { if (effective.canRename || canManage) setManagementOpen(true); return }
      if (id === 'back') { setManagementOpen(false); return }
      if (id === 'export') { close(); props.onExport(); return }
      if (id === 'self-nickname') { close(); props.onSelfNickname(); return }
      if (id === 'message-dnd') {
        close()
        changeMessageDnd(!messageDnd)
        return
      }
      if (id === 'ai-polish') { close(); props.onAiPolishOpen(); return }
      if (id === 'rename') {
        if (effective.canRename) { close(); props.onRename(actionTarget) }
        return
      }
      if (id === 'restrictions') {
        if (canManage) { close(); props.onRestrictionsOpen() }
        return
      }
      if (id === 'leave') { close(); void leaveOrDissolve() }
    }}
    anchor={<ArkmeConversationHeaderIconButton
      label={tr("群聊设置")}
      buttonRef={props.buttonRef}
      hasPopup
      expanded={props.open}
      busy={props.exportBusy}
      onClick={() => { props.onOpenChange(!props.open) }}
    ><ArkmeConversationMoreIcon /></ArkmeConversationHeaderIconButton>}
  />
}

function GroupJoinRestrictionsPanel(props: {
  source: ArkmeSourceItem
  open: boolean
  onClose: () => void
  onStatus: (message: string) => void
}) {
  useArkmeLocale()
  const [items, setItems] = useState<ArkmeGroupJoinRestrictionPage['items']>([])
  const [cursor, setCursor] = useState<string>()
  const [loading, setLoading] = useState(false)
  const [busyMemberRef, setBusyMemberRef] = useState('')
  const [loadError, setLoadError] = useState('')
  const [mutationError, setMutationError] = useState('')
  const [confirmationTarget, setConfirmationTarget] = useState<ArkmeGroupJoinRestrictionPage['items'][number]>()
  const requestRef = useRef<AbortController>()
  const mutationRef = useRef<AbortController>()
  const busyMemberRefRef = useRef('')
  const mountedRef = useRef(true)
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      requestRef.current?.abort()
      mutationRef.current?.abort()
    }
  }, [])

  const load = useCallback((nextCursor?: string) => {
    requestRef.current?.abort()
    const controller = new AbortController()
    requestRef.current = controller
    setLoading(true)
    setLoadError('')
    void callArkme<ArkmeGroupJoinRestrictionPage>('group.join-restrictions', {
      sourceRef: props.source.sourceRef,
      limit: 30,
      ...(nextCursor === undefined ? {} : { cursor: nextCursor }),
    }, controller.signal).then(page => {
      if (requestRef.current !== controller) return
      setItems(current => nextCursor === undefined
        ? page.items
        : [...current, ...page.items.filter(item => !current.some(existing => existing.memberRef === item.memberRef))])
      setCursor(page.nextCursor)
    }).catch(caught => {
      if (!controller.signal.aborted) setLoadError(errorMessage(caught) || '限制名单读取失败，请稍后重试')
    }).finally(() => {
      if (requestRef.current !== controller) return
      requestRef.current = undefined
      setLoading(false)
    })
  }, [props.source.sourceRef])

  useEffect(() => {
    if (!props.open) {
      setLoading(false)
      setBusyMemberRef('')
      setConfirmationTarget(undefined)
      return
    }
    setItems([])
    setCursor(undefined)
    setBusyMemberRef('')
    setLoadError('')
    setMutationError('')
    setConfirmationTarget(undefined)
    load()
    return () => {
      requestRef.current?.abort()
      mutationRef.current?.abort()
      busyMemberRefRef.current = ''
    }
  }, [load, props.open])

  useEffect(() => {
    if (!props.open || typeof document === 'undefined') return
    closeButtonRef.current?.focus({ preventScroll: true })
  }, [props.open])

  useEffect(() => {
    if (!props.open || typeof document === 'undefined') return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || confirmationTarget !== undefined || busyMemberRefRef.current !== '') return
      event.preventDefault()
      props.onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => { document.removeEventListener('keydown', onKeyDown) }
  }, [confirmationTarget, props.onClose, props.open])

  const liftRestriction = useCallback((item: ArkmeGroupJoinRestrictionPage['items'][number]) => {
    if (busyMemberRefRef.current !== '') return
    const controller = new AbortController()
    mutationRef.current = controller
    busyMemberRefRef.current = item.memberRef
    setBusyMemberRef(item.memberRef)
    setMutationError('')
    void callArkme<ArkmeGroupJoinRestrictionMutationResult>('group.join-restriction.set', {
      sourceRef: props.source.sourceRef,
      memberRef: item.memberRef,
      restricted: false,
    }, controller.signal).then(result => {
      if (!mountedRef.current || mutationRef.current !== controller) return
      if (result.restricted) {
        setMutationError('解除结果未确认，请重试')
        return
      }
      setItems(current => current.filter(value => value.memberRef !== item.memberRef))
      setConfirmationTarget(undefined)
      props.onStatus(`已解除对 ${item.displayName} 的加入限制`)
      globalThis.queueMicrotask(() => { closeButtonRef.current?.focus({ preventScroll: true }) })
    }).catch(caught => {
      if (mountedRef.current && mutationRef.current === controller && !controller.signal.aborted) {
        setMutationError(errorMessage(caught) || '解除限制失败，请稍后重试')
      }
    }).finally(() => {
      if (mutationRef.current !== controller) return
      mutationRef.current = undefined
      busyMemberRefRef.current = ''
      if (mountedRef.current) setBusyMemberRef('')
    })
  }, [props.onStatus, props.source.sourceRef])

  if (!props.open) return null
  return <>
    <div style={styles.drawerScrim} aria-hidden onPointerDown={event => {
      event.preventDefault()
      if (busyMemberRefRef.current === '') props.onClose()
    }} />
    <aside
      style={styles.restrictionDrawer}
      role="dialog"
      aria-modal="true"
      aria-labelledby="arkme-group-join-restrictions-title"
      aria-busy={busyMemberRef !== '' || undefined}
    >
      <ArkmeRightPanelHeader title={tr("禁止加入名单")} titleId="arkme-group-join-restrictions-title"
        onClose={props.onClose} closeLabel={tr("关闭禁止加入名单")} closeRef={closeButtonRef} closeDisabled={busyMemberRef !== ''} />
      <p style={styles.restrictionDescription}>{tr("名单中的用户无法通过邀请、添加或入群审批再次加入此群。")}</p>
      <div style={styles.restrictionBody}>
        {loading && items.length === 0 ? <div style={styles.loading}>{tr("正在读取限制名单…")}</div> : null}
        {!loading && loadError === '' && items.length === 0 ? <div style={styles.empty}>{tr("暂无被限制的用户")}</div> : null}
        {items.map(item => <div key={item.memberRef} style={styles.restrictionRow}>
          <Avatar imageRef={item.avatarRef} />
          <span style={styles.memberMain}>
            <span style={styles.memberName}>{item.displayName}</span>
            <span style={styles.restrictionStatus}>{tr("已禁止再次加入")}</span>
          </span>
          <button data-arkme-feedback="neutral"
            type="button"
            disabled={busyMemberRef !== ''}
            style={{ ...styles.restrictionAction, opacity: busyMemberRef === '' ? 1 : .45 }}
            onClick={() => { setMutationError(''); setConfirmationTarget(item) }}
          >{tr("解除限制")}</button>
        </div>)}
        {loadError === '' ? null : <div role="alert" style={styles.restrictionError}>
          <span style={{ minWidth: 0, flex: 1, overflowWrap: 'anywhere' }}>{loadError}</span>
          <button data-arkme-feedback="neutral" type="button" disabled={loading} style={styles.restrictionRetry} onClick={() => { load(cursor) }}>{tr("重试")}</button>
        </div>}
        {cursor !== undefined && loadError === '' ? <button data-arkme-feedback="neutral" type="button" disabled={loading} style={{ ...styles.restrictionLoadMore, opacity: loading ? .45 : 1 }} onClick={() => { load(cursor) }}>
          {loading ? tr("加载中…") : tr("加载更多")}
        </button> : null}
      </div>
    </aside>
    {confirmationTarget === undefined ? null : <ArkmeConfirmDialog
      titleId="arkme-group-join-restriction-lift-title"
      title={tr("解除加入限制？")}
      description={`解除后，${confirmationTarget.displayName} 不会自动加入群聊，仍需通过邀请、添加或入群审批。`}
      error={mutationError}
      busy={busyMemberRef === confirmationTarget.memberRef}
      confirmLabel={tr("解除限制")}
      busyLabel={tr("解除中…")}
      onClose={() => { setMutationError(''); setConfirmationTarget(undefined) }}
      onConfirm={() => { liftRestriction(confirmationTarget) }}
    />}
  </>
}

function RenameDialog(props: {
  source: ArkmeGroupActionTarget | undefined
  open: boolean
  onClose: () => void
  onSourceProjectionUpdated: (source: ArkmeSourceItem) => void
  onError: (message: string) => void
}) {
  useArkmeLocale()
  const [title, setTitle] = useState(props.source?.displayName ?? '')
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (props.open && props.source !== undefined) setTitle(props.source.displayName)
  }, [props.open, props.source])
  const source = props.source
  if (!props.open || source === undefined) return null
  return <div style={styles.dialogScrim} role="presentation" onMouseDown={event => {
    if (event.target === event.currentTarget) props.onClose()
  }}>
    <section style={styles.dialog} role="dialog" aria-modal="true" aria-label={tr("重命名")}>
      <h3 style={styles.dialogTitle}>{tr("重命名")}</h3>
      <input
        style={styles.dialogInput}
        value={title}
        maxLength={80}
        autoFocus
        aria-label={tr("群聊名称")}
        disabled={busy}
        onChange={event => { setTitle(event.target.value) }}
      />
      <div style={styles.dialogActions}>
        <button data-arkme-feedback="neutral" type="button" style={{ ...styles.dialogButton, background: colors.subtle, color: colors.text }} disabled={busy} onClick={props.onClose}>{tr("取消")}</button>
        <button data-arkme-feedback="primary"
          type="button"
          style={{ ...styles.dialogButton, background: colors.primary, color: arkmeTheme.foreground, opacity: busy || title.trim() === '' ? .55 : 1 }}
          disabled={busy || title.trim() === ''}
          onClick={() => {
            setBusy(true)
            void callArkme<ArkmeGroupProjectionResult>('group.rename', {
              sourceRef: source.sourceRef,
              title,
            })
              .then(result => { props.onSourceProjectionUpdated(result.source); props.onClose() })
              .catch(caught => { props.onError(errorMessage(caught)) })
              .finally(() => { setBusy(false) })
          }}
        >{tr("保存")}</button>
      </div>
    </section>
  </div>
}

export function ArkmeGroupChatControls(props: {
  accountScope: string | undefined
  membersOpen?: boolean
  onMembersOpenChange?: (open: boolean) => void
  source: ArkmeSourceItem
  overlayHostRef: RefObject<HTMLElement>
  onSourceProjectionUpdated: (source: ArkmeSourceItem) => void
  onMembershipChanged: (target: ArkmeGroupActionTarget) => void
  onMessageDndUpdated: (target: ArkmeGroupActionTarget, result: ArkmeGroupNotificationResult) => boolean
  onMemberOpen: (member: ArkmeConversationMemberItem) => void
  onMemberContextMenu: (member: ArkmeConversationMemberItem, anchorRect: DOMRect) => void
  onMemberHover?: ((member: ArkmeConversationMemberItem, anchor: HTMLElement, side: 'left') => void) | undefined
  aiPolishSettings?: ArkmeGroupAiPolishSnapshot | undefined
  onAiPolishSettingsChanged?: (settings: ArkmeGroupAiPolishSnapshot) => void
  onStatus?: (message: string) => void
  onError: (message: string) => void
  onExport?: () => void
  exportBusy?: boolean
  exportProcessed?: number
}) {
  useArkmeLocale()
  const [localMembersOpen, setLocalMembersOpen] = useState(false)
  const membersOpen = props.membersOpen ?? localMembersOpen
  const setMembersOpen = (open: boolean) => {
    setLocalMembersOpen(open)
    props.onMembersOpenChange?.(open)
  }
  const [inviteOpen, setInviteOpen] = useState(false)
  const [addMembersOpen, setAddMembersOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [aiPolishOpen, setAiPolishOpen] = useState(false)
  const [restrictionsOpen, setRestrictionsOpen] = useState(false)
  const [selfNicknameOpen, setSelfNicknameOpen] = useState(false)
  const [renameSource, setRenameSource] = useState<ArkmeGroupActionTarget>()
  const settingsButtonRef = useRef<HTMLButtonElement>(null)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])
  useEffect(() => {
    setRestrictionsOpen(false)
    setSelfNicknameOpen(false)
  }, [props.source.sourceRef, props.accountScope])
  const reportError = useCallback((message: string) => {
    if (mountedRef.current) props.onError(message)
  }, [props.onError])

  const openMembers = useCallback(() => {
    setSettingsOpen(false)
    setMembersOpen(true)
  }, [props.onMembersOpenChange])

  const overlayHost = props.overlayHostRef.current

  const toggleSettings = useCallback(() => {
    if (settingsOpen) {
      setSettingsOpen(false)
      return
    }
    setMembersOpen(false)
    setAiPolishOpen(false)
    setRestrictionsOpen(false)
    setSettingsOpen(true)
  }, [props.overlayHostRef, settingsOpen])

  const openAiPolish = useCallback(() => {
    setSettingsOpen(false)
    setAiPolishOpen(true)
  }, [])

  const openRestrictions = useCallback(() => {
    setSettingsOpen(false)
    setRestrictionsOpen(true)
  }, [])

  const closeRestrictions = useCallback(() => {
    setRestrictionsOpen(false)
    globalThis.queueMicrotask(() => { settingsButtonRef.current?.focus({ preventScroll: true }) })
  }, [])

  return <>
    <div style={styles.headerActions}>
      <ArkmeConversationHeaderIconButton label={tr("查看群成员")} onClick={openMembers}><ClientIcon src={icons.members} size={24} /></ArkmeConversationHeaderIconButton>
      <GroupSettingsMenu
        key={JSON.stringify([props.accountScope, props.source.sourceRef])}
        source={props.source}
        open={settingsOpen}
        buttonRef={settingsButtonRef}
        onOpenChange={open => {
          if (open) toggleSettings()
          else setSettingsOpen(false)
        }}
        aiPolishSettings={props.aiPolishSettings}
        onAiPolishSettingsChanged={settings => { props.onAiPolishSettingsChanged?.(settings) }}
        onClose={() => { setSettingsOpen(false) }}
        onRename={setRenameSource}
        onSelfNickname={() => { setSelfNicknameOpen(true) }}
        onAiPolishOpen={openAiPolish}
        onRestrictionsOpen={openRestrictions}
        onSourceProjectionUpdated={props.onSourceProjectionUpdated}
        onMembershipChanged={props.onMembershipChanged}
        onMessageDndUpdated={props.onMessageDndUpdated}
        onError={reportError}
        onExport={() => { props.onExport?.() }}
        exportBusy={props.exportBusy === true}
        exportProcessed={props.exportProcessed ?? 0}
      />
    </div>
    {overlayHost !== null && createPortal(<>
      <GroupAiPolishPanel
        source={props.source}
        open={aiPolishOpen}
        initialSettings={props.aiPolishSettings}
        onClose={() => { setAiPolishOpen(false) }}
        onSettingsChanged={settings => { props.onAiPolishSettingsChanged?.(settings) }}
        onError={reportError}
      />
      <GroupJoinRestrictionsPanel
        source={props.source}
        open={restrictionsOpen}
        onClose={closeRestrictions}
        onStatus={message => { props.onStatus?.(message) }}
      />
      {membersOpen && <GroupMembersDrawer
        source={props.source}
        open={membersOpen}
        accountScope={props.accountScope}
        onClose={() => { setMembersOpen(false) }}
        onAdd={() => { setInviteOpen(true) }}
        onMemberOpen={props.onMemberOpen}
        onMemberContextMenu={props.onMemberContextMenu}
        onMemberHover={props.onMemberHover}
        onError={reportError}
      />}
      <InviteCollaboratorsDialog
        source={props.source}
        open={inviteOpen}
        onClose={() => { setInviteOpen(false) }}
        onAddMembers={() => { setAddMembersOpen(true) }}
        onError={reportError}
      />
      <AddMembersDrawer
        source={props.source}
        open={addMembersOpen}
        onClose={() => { setAddMembersOpen(false) }}
        onAdded={() => {
          arkmeConversationMembers.invalidate(props.accountScope, props.source)
        }}
        onError={reportError}
      />
      {selfNicknameOpen && <ArkmeGroupSelfNicknameDialog
        key={JSON.stringify([props.accountScope, props.source.sourceRef])}
        source={props.source} accountScope={props.accountScope}
        onClose={() => { setSelfNicknameOpen(false); settingsButtonRef.current?.focus({ preventScroll: true }) }}
        onSaved={() => { props.onStatus?.('群昵称已修改') }}
      />}
      <RenameDialog
        source={renameSource}
        open={renameSource !== undefined}
        onClose={() => { setRenameSource(undefined) }}
        onSourceProjectionUpdated={props.onSourceProjectionUpdated}
        onError={reportError}
      />
    </>, overlayHost)}
  </>
}
